import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Calendar,
  Users,
  Settings,
  FileText,
  LogOut,
  Plus,
  Trash2,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Download,
  Edit,
  Save,
  X,
  Check,
  Menu,
  RefreshCw,
  Search,
  UserCheck,
} from "lucide-react";
import { API, DAYS, TIMES, formatDate, getTimesForDayType, ALL_TIMES, SATURDAY_TIMES, SATURDAY_DATES } from "@/lib/utils";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pool-lesson-book/artifacts/c9s2nl0s_rr%20logo.png";

// Generate dates for the camp season (June 29 - August 20, 2026) including Saturdays
const generateCalendarDates = () => {
  const dates = [];
  const start = new Date(2026, 5, 29); // June 29
  const end = new Date(2026, 7, 20); // August 20
  const fridayStart = new Date(2026, 6, 10); // July 10 - first Friday
  const skipDates = [
    new Date(2026, 6, 3).toDateString(), // July 3
    new Date(2026, 6, 4).toDateString(), // July 4
  ];
  
  // Saturday dates (pre-defined, excluding July 4th weekend)
  const saturdayDateStrings = new Set(SATURDAY_DATES);
  
  let current = new Date(start);
  // Include the Saturday before camp starts (June 27)
  const june27 = new Date(2026, 5, 27);
  if (saturdayDateStrings.has(formatDate(june27))) {
    dates.push(new Date(june27));
  }
  
  while (current <= end) {
    const dayOfWeek = current.getDay();
    const isFriday = dayOfWeek === 5;
    const isSaturday = dayOfWeek === 6;
    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
    const isSkipped = skipDates.includes(current.toDateString());
    
    if (isSaturday) {
      // Include Saturday if it's in the predefined Saturday dates
      if (saturdayDateStrings.has(formatDate(current))) {
        dates.push(new Date(current));
      }
    } else if (isWeekday && !isSkipped) {
      // Fridays only start from July 10
      if (!isFriday || current >= fridayStart) {
        dates.push(new Date(current));
      }
    }
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const CALENDAR_DATES = generateCalendarDates();

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("calendar");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [lifeguards, setLifeguards] = useState([]);
  const [settings, setSettings] = useState({ max_capacity_per_slot: 10 });
  const [earnings, setEarnings] = useState({ camp_totals: null, by_instructor: [], lessons_detail: [] });
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [selectedNeRegistration, setSelectedNeRegistration] = useState(null);
  const [newLifeguard, setNewLifeguard] = useState("");
  const [newLifeguardAge, setNewLifeguardAge] = useState("");
  const [newLifeguardGender, setNewLifeguardGender] = useState("");
  const [newLifeguardAgeGroup, setNewLifeguardAgeGroup] = useState("");
  const [newLifeguardAvailability, setNewLifeguardAvailability] = useState([]);
  const [editingLifeguardId, setEditingLifeguardId] = useState(null);
  const [editAvailability, setEditAvailability] = useState([]);
  const [editAgeGroup, setEditAgeGroup] = useState("");
  const [lessonAction, setLessonAction] = useState(null); // {date, action: 'delete'|'reschedule'}
  const [lessonReason, setLessonReason] = useState("camp_cancelled");
  const [lessonRefund, setLessonRefund] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduleInstructor, setRescheduleInstructor] = useState("");
  const [enrolledCampers, setEnrolledCampers] = useState({ campers: [], last_sync: null, count: 0 });
  const [enrolledSearch, setEnrolledSearch] = useState("");
  const [camperSearch, setCamperSearch] = useState("");
  const [camperStatusFilter, setCamperStatusFilter] = useState("all");
  const [syncingCampminder, setSyncingCampminder] = useState(false);
  const [reportDate, setReportDate] = useState("");
  const [filterDay, setFilterDay] = useState("all");
  const [filterTime, setFilterTime] = useState("all");
  const [filterInstructor, setFilterInstructor] = useState("all");
  // Non-enrolled state
  const [neRegistrations, setNeRegistrations] = useState([]);
  const [neLessonSlots, setNeLessonSlots] = useState([]);
  const [neLifeguards, setNeLifeguards] = useState([]);
  const [neSettings, setNeSettings] = useState({});
  const [neSummary, setNeSummary] = useState({});
  const [neNewLgName, setNeNewLgName] = useState("");
  const [neNewLgType, setNeNewLgType] = useState("");
  const [neEditLg, setNeEditLg] = useState(null); // { id, name, age_group, active, notes }
  // Daily Roster PDF download modal
  const [rosterOpen, setRosterOpen] = useState(false);
  const [rosterSeason, setRosterSeason] = useState("camp"); // 'camp' | 'non_enrolled'
  const [rosterSession, setRosterSession] = useState("june"); // 'june' | 'july_august'
  const [rosterDate, setRosterDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rosterWholeWeek, setRosterWholeWeek] = useState(false);
  // Half-season private invites
  const [hsInvites, setHsInvites] = useState([]);
  const [hsForm, setHsForm] = useState({
    child_first_name: "", child_last_name: "", parent_email: "",
    lifeguard_id: "", time: "8:30am",
    dates: ["", "", "", ""],
  });
  const [hsSubmitting, setHsSubmitting] = useState(false);
  const fetchHsInvites = async () => {
    try {
      const res = await axios.get(`${API}/admin/half-season/invites`);
      setHsInvites(res.data || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => {
    if (activeTab === "half_season") fetchHsInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  // Active session for the Non-Enrolled admin views (june|july_august)
  const [neSession, setNeSession] = useState("june");
  const NE_SESSION_DEFAULTS = {
    june: { season_start_date: "2026-06-01", season_end_date: "2026-06-26", saturday_allowed: true },
    july_august: { season_start_date: "2026-06-29", season_end_date: "2026-08-20", saturday_allowed: false },
  };
  const neSessionLabel = neSession === "july_august" ? "July-August (In Season)" : "June (Pre Season)";
  const [neNewSlot, setNeNewSlot] = useState({ day_of_week: "", time: "", lifeguard_id: "", price: "", max_capacity: "1" });
  const [neBulk, setNeBulk] = useState({
    lifeguard_id: "",
    availability: [],  // [{day, time}]
    price_per_lesson: 75,
    season_start_date: "2026-06-01",
    season_end_date: "2026-06-26",
    replace_existing: false,
  });

  const NE_DAYS = [
    { key: "monday", short: "Monday" },
    { key: "tuesday", short: "Tuesday" },
    { key: "wednesday", short: "Wednesday" },
    { key: "thursday", short: "Thursday" },
    { key: "friday", short: "Friday" },
  ];
  const NE_TIMES = ["2:00pm", "2:30pm", "3:00pm", "3:30pm", "4:00pm", "4:30pm", "5:00pm"];

  const isNeAvailSelected = (day, time) =>
    neBulk.availability.some(s => s.day === day && s.time === time);
  const toggleNeAvail = (day, time) => {
    setNeBulk(p => {
      const exists = p.availability.some(s => s.day === day && s.time === time);
      return {
        ...p,
        availability: exists
          ? p.availability.filter(s => !(s.day === day && s.time === time))
          : [...p.availability, { day, time }]
      };
    });
  };

  useEffect(() => {
    const isAuth = localStorage.getItem("admin_authenticated");
    if (!isAuth) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [navigate]);

  // Refetch NE data whenever the session toggle changes (lightweight, NE-only)
  useEffect(() => {
    const isAuth = localStorage.getItem("admin_authenticated");
    if (!isAuth) return;
    fetchNeData();
    setNeBulk(p => ({
      ...p,
      availability: [],
      season_start_date: NE_SESSION_DEFAULTS[neSession].season_start_date,
      season_end_date: NE_SESSION_DEFAULTS[neSession].season_end_date,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neSession]);

  const fetchNeData = async () => {
    // Lightweight refetch for the NE tabs only — used on session toggle to avoid re-pulling enrolled data.
    try {
      const [neRegs, neSlots, neLgs, neSett, neSum] = await Promise.all([
        axios.get(`${API}/admin/non-enrolled/registrations?session=${neSession}`),
        axios.get(`${API}/admin/non-enrolled/lesson-slots?session=${neSession}`),
        axios.get(`${API}/admin/non-enrolled/lifeguards`),
        axios.get(`${API}/admin/non-enrolled/settings`),
        axios.get(`${API}/admin/non-enrolled/reports/summary?session=${neSession}`),
      ]);
      setNeRegistrations(neRegs.data);
      setNeLessonSlots(neSlots.data);
      setNeLifeguards(neLgs.data);
      setNeSettings(neSett.data);
      setNeSummary(neSum.data);
    } catch (e) { console.error("Non-enrolled fetch:", e); }
  };

  const fetchData = async () => {
    try {
      const [regsRes, calRes, lgRes, settingsRes, earningsRes, enrolledRes] = await Promise.all([
        axios.get(`${API}/admin/registrations`),
        axios.get(`${API}/admin/calendar`),
        axios.get(`${API}/admin/lifeguards`),
        axios.get(`${API}/admin/settings`),
        axios.get(`${API}/admin/lifeguard-earnings`),
        axios.get(`${API}/admin/enrolled-campers`),
      ]);
      setRegistrations(regsRes.data);
      setCalendarData(calRes.data);
      setLifeguards(lgRes.data);
      setSettings(settingsRes.data);
      setEarnings(earningsRes.data);
      setEnrolledCampers(enrolledRes.data);
      // Non-enrolled data
      try {
        const [neRegs, neSlots, neLgs, neSett, neSum] = await Promise.all([
          axios.get(`${API}/admin/non-enrolled/registrations?session=${neSession}`),
          axios.get(`${API}/admin/non-enrolled/lesson-slots?session=${neSession}`),
          axios.get(`${API}/admin/non-enrolled/lifeguards`),  // global, no session
          axios.get(`${API}/admin/non-enrolled/settings`),     // global, no session
          axios.get(`${API}/admin/non-enrolled/reports/summary?session=${neSession}`),
        ]);
        setNeRegistrations(neRegs.data);
        setNeLessonSlots(neSlots.data);
        setNeLifeguards(neLgs.data);
        setNeSettings(neSett.data);
        setNeSummary(neSum.data);
      } catch (e) { console.error("Non-enrolled fetch:", e); }
    } catch (err) {
      console.error("Failed to fetch data:", err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_authenticated");
    navigate("/admin");
  };

  const handleMarkPaid = async (regId, paid) => {
    try {
      await axios.put(`${API}/admin/registration/${regId}/paid`, { paid });
      toast.success(paid ? "Marked as paid" : "Payment status reverted");
      fetchData();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleAssignInstructor = async (regId, instructor) => {
    try {
      await axios.put(`${API}/admin/registration/${regId}/instructor`, { instructor });
      toast.success("Instructor assigned");
      fetchData();
    } catch (err) {
      toast.error("Failed to assign instructor");
    }
  };

  const handleConfirmChoice = async (regId, choice) => {
    try {
      const response = await axios.put(`${API}/admin/registration/${regId}/confirm-choice`, { choice });
      toast.success("Lesson schedule confirmed and email sent to parent");
      
      // Update the selected registration immediately to show confirmed state
      if (selectedRegistration && selectedRegistration.id === regId) {
        const { confirmed_day, confirmed_time, confirmed_price } = response.data;
        const confirmed_dates = choice === 1 
          ? selectedRegistration.choice1_dates 
          : selectedRegistration.choice2_dates;
        
        setSelectedRegistration({
          ...selectedRegistration,
          confirmed_choice: choice,
          confirmed_day,
          confirmed_time,
          confirmed_price,
          confirmed_dates,
          status: selectedRegistration.signature ? "confirmed" : selectedRegistration.status
        });
      }
      
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to confirm choice");
    }
  };

  const handleDeleteRegistration = async (regId) => {
    if (!window.confirm("Are you sure you want to delete this registration? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/registration/${regId}`);
      toast.success("Registration deleted");
      setSelectedRegistration(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to delete registration");
    }
  };

  const handleClearAllRegistrations = async () => {
    const pw = window.prompt("Enter admin password to clear ALL registrations:");
    if (!pw) return;
    try {
      await axios.delete(`${API}/admin/registrations/clear-all`, { data: { password: pw } });
      toast.success("All registrations cleared");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear registrations");
    }
  };

  const handleSyncCampminder = async () => {
    setSyncingCampminder(true);
    try {
      const res = await axios.post(`${API}/admin/sync-campminder`);
      toast.success(res.data.message);
      const enrolledRes = await axios.get(`${API}/admin/enrolled-campers`);
      setEnrolledCampers(enrolledRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync CampMinder data");
    } finally {
      setSyncingCampminder(false);
    }
  };

  const handleDeleteLesson = async (regId, date, reason, refund) => {
    try {
      const res = await axios.post(`${API}/admin/registration/${regId}/delete-lesson`, { date, reason, refund });
      toast.success(`Lesson on ${date} deleted`);
      // Update the selected registration in place
      if (selectedRegistration && selectedRegistration.id === regId) {
        setSelectedRegistration({
          ...selectedRegistration,
          confirmed_dates: res.data.remaining_dates,
          choice1_dates: res.data.remaining_dates,
          lesson_modifications: [...(selectedRegistration.lesson_modifications || []), res.data.modification]
        });
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete lesson");
    }
  };

  const handleRescheduleLesson = async (regId, originalDate, newDate, newTime, newInstructor, reason) => {
    try {
      const res = await axios.post(`${API}/admin/registration/${regId}/reschedule-lesson`, {
        original_date: originalDate,
        new_date: newDate,
        new_time: newTime || null,
        new_instructor: newInstructor || null,
        reason
      });
      toast.success(`Lesson rescheduled to ${newDate}`);
      if (selectedRegistration && selectedRegistration.id === regId) {
        setSelectedRegistration({
          ...selectedRegistration,
          confirmed_dates: res.data.updated_dates,
          choice1_dates: res.data.updated_dates,
          confirmed_time: newTime || selectedRegistration.confirmed_time,
          assigned_instructor: newInstructor || selectedRegistration.assigned_instructor,
          lesson_modifications: [...(selectedRegistration.lesson_modifications || []), res.data.modification]
        });
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reschedule lesson");
    }
  };

  const handleAddLifeguard = async () => {
    if (!newLifeguard.trim()) return;
    try {
      await axios.post(`${API}/admin/lifeguards`, {
        name: newLifeguard,
        age: parseInt(newLifeguardAge) || null,
        gender: newLifeguardGender || null,
        age_group: newLifeguardAgeGroup || null,
        availability: newLifeguardAvailability
      });
      setNewLifeguard("");
      setNewLifeguardAge("");
      setNewLifeguardGender("");
      setNewLifeguardAgeGroup("");
      setNewLifeguardAvailability([]);
      toast.success("Lifeguard added");
      fetchData();
    } catch (err) {
      toast.error("Failed to add lifeguard");
    }
  };

  const toggleNewAvailability = (day, time) => {
    setNewLifeguardAvailability(prev => {
      const exists = prev.some(s => s.day === day && s.time === time);
      if (exists) return prev.filter(s => !(s.day === day && s.time === time));
      return [...prev, { day, time }];
    });
  };

  const isNewSlotSelected = (day, time) => {
    return newLifeguardAvailability.some(s => s.day === day && s.time === time);
  };

  const startEditAvailability = (lg) => {
    setEditingLifeguardId(lg.id);
    setEditAvailability(lg.availability || []);
    setEditAgeGroup(lg.age_group || "");
  };

  const toggleEditAvailability = (day, time) => {
    setEditAvailability(prev => {
      const exists = prev.some(s => s.day === day && s.time === time);
      if (exists) return prev.filter(s => !(s.day === day && s.time === time));
      return [...prev, { day, time }];
    });
  };

  const isEditSlotSelected = (day, time) => {
    return editAvailability.some(s => s.day === day && s.time === time);
  };

  const saveEditAvailability = async (lifeguardId) => {
    try {
      await axios.put(`${API}/admin/lifeguards/${lifeguardId}`, {
        availability: editAvailability,
        age_group: editAgeGroup || null
      });
      setEditingLifeguardId(null);
      toast.success("Lifeguard updated");
      fetchData();
    } catch (err) {
      toast.error("Failed to update lifeguard");
    }
  };

  const handleDeleteLifeguard = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lifeguard?")) return;
    try {
      await axios.delete(`${API}/admin/lifeguards/${id}`);
      toast.success("Lifeguard deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete lifeguard");
    }
  };

  const handleUpdateCapacity = async (capacity) => {
    try {
      await axios.put(`${API}/admin/settings/capacity?max_capacity=${capacity}`);
      toast.success("Capacity updated");
      fetchData();
    } catch (err) {
      toast.error("Failed to update capacity");
    }
  };

  const handleGenerateReport = async () => {
    if (!reportDate) {
      toast.error("Please select a date");
      return;
    }
    try {
      const response = await axios.get(`${API}/admin/report/${reportDate}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${reportDate.replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Report downloaded");
    } catch (err) {
      toast.error("Failed to generate report");
    }
  };

  const getSlotStatus = (dateStr, time) => {
    const slot = calendarData.find(s => s.date === dateStr && s.time === time);
    if (!slot || slot.registrations.length === 0) return "open";
    
    const statuses = slot.registrations.map(r => r.status);
    // If ALL are paid, show red (paid)
    if (statuses.every(s => s === "paid")) return "paid";
    // If any are pending (reserved/signed), show yellow
    if (statuses.some(s => s === "reserved" || s === "signed")) return "reserved";
    // Default to paid if all confirmed
    return "paid";
  };

  const getSlotCount = (dateStr, time) => {
    const slot = calendarData.find(s => s.date === dateStr && s.time === time);
    return slot ? slot.registrations.length : 0;
  };

  const getSlotRegistrations = (dateStr, time) => {
    const slot = calendarData.find(s => s.date === dateStr && s.time === time);
    return slot ? slot.registrations : [];
  };

  // Filter calendar data
  const filteredDates = CALENDAR_DATES.filter(date => {
    if (filterDay !== "all") {
      const dow = date.getDay();
      const dayName = dow === 0 ? "sunday" : dow === 6 ? "saturday" : DAYS[dow - 1];
      if (dayName !== filterDay) return false;
    }
    return true;
  });

  const statusBadge = (status) => {
    switch (status) {
      case "paid":
        return <span className="badge-red">Paid</span>;
      case "confirmed":
        return <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-bold uppercase">Confirmed</span>;
      case "signed":
        return <span className="badge-yellow">Signed</span>;
      case "reserved":
        return <span className="badge-yellow">Reserved</span>;
      default:
        return <span className="badge-green">Open</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-16 h-16 border-4 border-[#109FE9] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4 py-3">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
          <Menu className="w-6 h-6 text-slate-700" />
        </button>
        <img src={LOGO_URL} alt="Rolling River" className="h-8" />
        <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="mobile-logout-btn">
          <LogOut className="w-5 h-5" />
        </Button>
      </div>

      {/* Sidebar Overlay (mobile) */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/40 z-50" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 z-50 transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <img src={LOGO_URL} alt="Rolling River" className="h-12 mx-auto" />
          <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <nav className="p-4 space-y-2 overflow-y-auto pb-8" style={{maxHeight: "calc(100vh - 220px)"}}>
          {/* Enrolled Section */}
          <p className="text-xs font-bold text-[#2B4C8C] uppercase tracking-wider px-3 mb-2">Enrolled (In Season)</p>
          {[
            { key: "calendar", icon: Calendar, label: "Calendar" },
            { key: "campers", icon: Users, label: "Campers" },
            { key: "enrolled", icon: UserCheck, label: "Enrolled" },
            { key: "half_season", icon: Users, label: "Half-Season Invites" },
            { key: "lifeguards", icon: Users, label: "Lifeguards" },
            { key: "earnings", icon: DollarSign, label: "Earnings" },
            { key: "settings", icon: Settings, label: "Settings" },
            { key: "reports", icon: FileText, label: "Reports" },
          ].map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => { setActiveTab(key); setSidebarOpen(false); }}
              className={`admin-nav-item w-full text-left flex items-center gap-3 ${activeTab === key ? "active" : ""}`}
              data-testid={`nav-${key}`}
            >
              <Icon className="w-5 h-5" /> {label}
            </button>
          ))}

          {/* Non-Enrolled Section */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="text-xs font-bold text-[#D4A843] uppercase tracking-wider px-3 mb-2">Non-Enrolled (Pre Season / In Season)</p>
            {[
              { key: "ne_dashboard", icon: DollarSign, label: "Dashboard" },
              { key: "ne_calendar", icon: Calendar, label: "Calendar" },
              { key: "ne_registrations", icon: Users, label: "Registrations" },
              { key: "ne_slots", icon: Calendar, label: "Lesson Slots" },
              { key: "ne_lifeguards", icon: Users, label: "Lifeguards" },
              { key: "ne_earnings", icon: DollarSign, label: "Earnings" },
              { key: "ne_reports", icon: FileText, label: "Reports" },
              { key: "ne_settings", icon: Settings, label: "Settings" },
            ].map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => { setActiveTab(key); setSidebarOpen(false); }}
                className={`admin-nav-item w-full text-left flex items-center gap-3 ${activeTab === key ? "active" : ""}`}
                data-testid={`nav-${key}`}
              >
                <Icon className="w-5 h-5" /> {label}
              </button>
            ))}
          </div>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100 hidden lg:block space-y-2">
          <Button
            variant="default"
            className="w-full justify-start bg-[#D4A843] hover:bg-[#b8902e] text-white font-bold"
            onClick={() => setRosterOpen(true)}
            data-testid="open-roster-pdf-btn"
          >
            <Download className="w-5 h-5 mr-2" /> Daily Roster PDF
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 md:p-6 lg:p-8 pt-16 lg:pt-8">
        {/* Calendar Tab */}
        {activeTab === "calendar" && (
          <div className="animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800">
                Lesson Calendar
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-[#DCFCE7] border-2 border-[#22C55E]" />
                  <span className="text-xs text-slate-600">Open</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-[#FEF3C7] border-2 border-[#EAB308]" />
                  <span className="text-xs text-slate-600">Reserved</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-[#FEE2E2] border-2 border-[#EF4444]" />
                  <span className="text-xs text-slate-600">Paid</span>
                </div>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleClearAllRegistrations}
                  className="bg-red-500 hover:bg-red-600 ml-2"
                  data-testid="clear-all-calendar-btn"
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Clear All
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Select value={filterDay} onValueChange={setFilterDay}>
                <SelectTrigger className="w-full sm:w-40" data-testid="filter-day">
                  <SelectValue placeholder="Filter by day" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Days</SelectItem>
                  {DAYS.map(day => (
                    <SelectItem key={day} value={day}>
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterTime} onValueChange={setFilterTime}>
                <SelectTrigger className="w-full sm:w-40" data-testid="filter-time">
                  <SelectValue placeholder="Filter by time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Times</SelectItem>
                  {ALL_TIMES.map(time => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterInstructor} onValueChange={setFilterInstructor}>
                <SelectTrigger className="w-full sm:w-48" data-testid="filter-instructor">
                  <SelectValue placeholder="Filter by instructor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Instructors</SelectItem>
                  {lifeguards.map(lg => (
                    <SelectItem key={lg.id} value={lg.name}>{lg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Calendar Grid */}
            <div className="card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="p-3 text-left text-sm font-semibold text-slate-600">Date</th>
                    {(filterTime === "all" ? ALL_TIMES : [filterTime]).map(time => (
                      <th key={time} className="p-3 text-center text-sm font-semibold text-slate-600">
                        {time}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredDates.map(date => {
                    const dateStr = formatDate(date);
                    const dayOfWeek = date.getDay();
                    const dayName = dayOfWeek === 0 ? "sunday" : dayOfWeek === 6 ? "saturday" : DAYS[dayOfWeek - 1];
                    const dayTimes = getTimesForDayType(dayName);
                    
                    return (
                      <tr key={dateStr} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3">
                          <div className="font-semibold text-slate-800">{dateStr}</div>
                          <div className="text-xs text-slate-500">{dayName?.charAt(0).toUpperCase() + dayName?.slice(1)}</div>
                        </td>
                        {(filterTime === "all" ? ALL_TIMES : [filterTime]).map(time => {
                          // If this time doesn't apply to this day type, show empty
                          if (!dayTimes.includes(time)) {
                            return (
                              <td key={time} className="p-2">
                                <div className="calendar-cell bg-slate-50 text-center opacity-30">
                                  <span className="text-xs text-slate-300">—</span>
                                </div>
                              </td>
                            );
                          }
                          const status = getSlotStatus(dateStr, time);
                          const count = getSlotCount(dateStr, time);
                          const regs = getSlotRegistrations(dateStr, time);
                          
                          // Apply instructor filter
                          const filteredRegs = filterInstructor === "all" 
                            ? regs 
                            : regs.filter(r => r.instructor === filterInstructor);
                          
                          if (filterInstructor !== "all" && filteredRegs.length === 0) {
                            return (
                              <td key={time} className="p-2">
                                <div className="calendar-cell calendar-cell-open text-center">
                                  <span className="text-xs text-slate-400">-</span>
                                </div>
                              </td>
                            );
                          }

                          return (
                            <td key={time} className="p-2">
                              <div
                                className={`calendar-cell ${
                                  count === 0 ? "calendar-cell-open" :
                                  status === "paid" ? "calendar-cell-paid" :
                                  status === "signed" ? "calendar-cell-signed" :
                                  "calendar-cell-reserved"
                                }`}
                                onClick={() => setSelectedSlot({ date: dateStr, time, registrations: regs })}
                                data-testid={`slot-${dateStr}-${time}`}
                              >
                                {count === 0 ? (
                                  <div className="text-center">
                                    <span className="text-xs text-slate-400">Open</span>
                                  </div>
                                ) : (
                                  <div className="text-left space-y-1">
                                    {(filterInstructor !== "all" ? filteredRegs : regs).map(r => (
                                      <div key={r.id} className={`text-xs px-1.5 py-0.5 rounded ${
                                        r.status === "paid" ? "bg-red-200 text-red-900" :
                                        r.status === "signed" ? "bg-yellow-200 text-yellow-900" :
                                        "bg-yellow-100 text-yellow-800"
                                      }`}>
                                        {r.child_name}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Campers Tab */}
        {activeTab === "campers" && (
          <div className="animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800">
                Registered Campers ({registrations.length})
              </h1>
              <div className="flex gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-initial">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="camper-search"
                    placeholder="Search by child name..."
                    value={camperSearch || ""}
                    onChange={(e) => setCamperSearch(e.target.value)}
                    className="pl-9 w-full sm:w-64"
                  />
                </div>
                <Select value={camperStatusFilter || "all"} onValueChange={setCamperStatusFilter}>
                  <SelectTrigger className="w-36" data-testid="camper-status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="reserved">Reserved</SelectItem>
                    <SelectItem value="signed">Signed</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="released">Released</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  variant="destructive" 
                  onClick={handleClearAllRegistrations}
                  className="bg-red-500 hover:bg-red-600 flex-shrink-0"
                  data-testid="clear-all-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Clear All
                </Button>
              </div>
            </div>

            <div className="grid gap-4">
              {registrations
                .filter(reg => {
                  if (camperStatusFilter && camperStatusFilter !== "all" && reg.status !== camperStatusFilter) return false;
                  if (!camperSearch) return true;
                  const q = camperSearch.toLowerCase();
                  const childName = `${reg.child_first_name} ${reg.child_last_name}`.toLowerCase();
                  return childName.includes(q);
                })
                .sort((a, b) => {
                  const nameA = `${a.child_last_name} ${a.child_first_name}`.toLowerCase();
                  const nameB = `${b.child_last_name} ${b.child_first_name}`.toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .map(reg => (
                <div
                  key={reg.id}
                  className="card hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedRegistration(reg)}
                  data-testid={`camper-card-${reg.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-base md:text-lg text-slate-800">
                        {reg.child_first_name} {reg.child_last_name}
                      </h3>
                      <p className="text-sm text-slate-600">
                        Parent: {reg.parent_first_name} {reg.parent_last_name}
                      </p>
                      <p className="text-sm text-slate-500 break-words">
                        {(reg.confirmed_day || reg.choice1_day)?.charAt(0).toUpperCase() + (reg.confirmed_day || reg.choice1_day)?.slice(1)} at {reg.confirmed_time || reg.choice1_time}
                        <span className="ml-2 text-slate-400">| {reg.assigned_instructor || reg.lifeguard_preference}</span>
                      </p>
                      <div className="mt-2 text-xs text-slate-400 break-words">
                        <p><strong>Dates:</strong> {(reg.confirmed_dates || reg.choice1_dates)?.join(", ")}</p>
                      </div>
                    </div>
                    <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:text-right flex-shrink-0">
                      {statusBadge(reg.status)}
                      <p className="text-lg font-bold text-slate-800">
                        ${(reg.confirmed_price || reg.choice1_price || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {registrations.length === 0 && (
                <div className="card text-center py-12 text-slate-500">
                  No registrations yet
                </div>
              )}
            </div>
          </div>
        )}

        {/* Enrolled Campers Tab */}
        {activeTab === "enrolled" && (
          <div className="animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
              <div>
                <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800">
                  Enrolled Campers
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                  {enrolledCampers.count} campers from CampMinder (Mariner, Skipper, Navigator)
                  {enrolledCampers.last_sync && (
                    <span> &middot; Last synced: {new Date(enrolledCampers.last_sync).toLocaleString()}</span>
                  )}
                </p>
              </div>
              <Button
                onClick={handleSyncCampminder}
                disabled={syncingCampminder}
                className="bg-[#109FE9] hover:bg-[#0E8ACB] text-white"
                data-testid="sync-campminder-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncingCampminder ? "animate-spin" : ""}`} />
                {syncingCampminder ? "Syncing..." : "Sync CampMinder"}
              </Button>
            </div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search campers by name..."
                value={enrolledSearch}
                onChange={(e) => setEnrolledSearch(e.target.value)}
                className="pl-10"
                data-testid="enrolled-search"
              />
            </div>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="max-h-[600px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">#</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">First Name</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Last Name</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Division</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrolledCampers.campers
                      .filter(c => {
                        if (!enrolledSearch) return true;
                        const q = enrolledSearch.toLowerCase();
                        return c.first_name.toLowerCase().includes(q) || c.last_name.toLowerCase().includes(q) || (c.division || '').toLowerCase().includes(q);
                      })
                      .sort((a, b) => a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name))
                      .map((camper, idx) => (
                        <tr key={camper.person_id || idx} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`enrolled-camper-${idx}`}>
                          <td className="px-4 py-2 text-slate-400">{idx + 1}</td>
                          <td className="px-4 py-2 text-slate-800">{camper.first_name}</td>
                          <td className="px-4 py-2 text-slate-800">{camper.last_name}</td>
                          <td className="px-4 py-2">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              camper.division === 'Navigator' || camper.division === 'CIT' 
                                ? 'bg-amber-100 text-amber-800' 
                                : camper.division === 'Mariner' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-green-100 text-green-800'
                            }`}>
                              {camper.division || 'Unknown'}
                            </span>
                          </td>
                        </tr>
                      ))
                    }
                    {enrolledCampers.campers.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                          No enrolled campers. Click "Sync CampMinder" to fetch data.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Half-Season Invites Tab */}
        {activeTab === "half_season" && (
          <div className="animate-fade-in" data-testid="half-season-tab">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800 mb-2">Half-Season Invites</h1>
            <p className="text-sm text-slate-500 mb-6">
              Create a private signup link for an enrolled camper who only wants part of the season.
              Auto-priced at <b>$45/lesson</b> × number of dates. After you send the email, the parent goes through
              the same sign + pay flow as a regular enrolled registration.
            </p>

            <div className="card mb-6 max-w-2xl" data-testid="hs-create-card">
              <h2 className="font-bold text-slate-800 mb-4">Create New Invite</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Child First Name *</Label>
                  <Input data-testid="hs-child-first" value={hsForm.child_first_name}
                    onChange={e => setHsForm({...hsForm, child_first_name: e.target.value})}/>
                </div>
                <div>
                  <Label>Child Last Name *</Label>
                  <Input data-testid="hs-child-last" value={hsForm.child_last_name}
                    onChange={e => setHsForm({...hsForm, child_last_name: e.target.value})}/>
                </div>
                <div className="md:col-span-2">
                  <Label>Parent Email *</Label>
                  <Input type="email" data-testid="hs-parent-email" value={hsForm.parent_email}
                    onChange={e => setHsForm({...hsForm, parent_email: e.target.value})}/>
                </div>
                <div>
                  <Label>Lifeguard *</Label>
                  <Select value={hsForm.lifeguard_id} onValueChange={v => setHsForm({...hsForm, lifeguard_id: v})}>
                    <SelectTrigger data-testid="hs-lifeguard"><SelectValue placeholder="Select lifeguard"/></SelectTrigger>
                    <SelectContent>
                      {(lifeguards || []).map(lg => (
                        <SelectItem key={lg.id} value={lg.id}>{lg.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Time *</Label>
                  <Select value={hsForm.time} onValueChange={v => setHsForm({...hsForm, time: v})}>
                    <SelectTrigger data-testid="hs-time"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="8:30am">8:30am</SelectItem>
                      <SelectItem value="4:00pm">4:00pm</SelectItem>
                      <SelectItem value="4:30pm">4:30pm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Lesson Dates ({hsForm.dates.filter(Boolean).length} selected)</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
                    {hsForm.dates.map((d, i) => (
                      <Input
                        key={i}
                        type="date"
                        data-testid={`hs-date-${i}`}
                        value={d}
                        onChange={e => {
                          const nd = [...hsForm.dates]; nd[i] = e.target.value; setHsForm({...hsForm, dates: nd});
                        }}
                      />
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button type="button" className="text-xs text-slate-500 hover:underline"
                      onClick={() => setHsForm({...hsForm, dates: [...hsForm.dates, ""]})}>
                      + Add another date
                    </button>
                    {hsForm.dates.length > 1 && (
                      <button type="button" className="text-xs text-slate-500 hover:underline"
                        onClick={() => setHsForm({...hsForm, dates: hsForm.dates.slice(0, -1)})}>
                        − Remove last
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <b className="text-amber-800">Auto Total:</b>{" "}
                <span className="font-bold text-[#2B4C8C]">
                  ${(hsForm.dates.filter(Boolean).length * 45).toFixed(2)}
                </span>
                <span className="text-slate-500"> ($45/lesson × {hsForm.dates.filter(Boolean).length} dates)</span>
              </div>

              <Button
                className="w-full mt-4 bg-[#2B4C8C] hover:bg-[#1e3666] text-white font-bold"
                data-testid="hs-send-btn"
                disabled={hsSubmitting}
                onClick={async () => {
                  const dates = hsForm.dates.filter(Boolean);
                  if (!hsForm.child_first_name || !hsForm.child_last_name || !hsForm.parent_email || !hsForm.lifeguard_id || dates.length < 1) {
                    toast.error("Fill in child name, parent email, lifeguard, and at least one date.");
                    return;
                  }
                  setHsSubmitting(true);
                  try {
                    const res = await axios.post(`${API}/admin/half-season/invites`, {
                      child_first_name: hsForm.child_first_name,
                      child_last_name: hsForm.child_last_name,
                      parent_email: hsForm.parent_email,
                      lifeguard_id: hsForm.lifeguard_id,
                      time: hsForm.time,
                      dates,
                    });
                    toast.success(`Invite sent to ${hsForm.parent_email}`);
                    setHsForm({
                      child_first_name: "", child_last_name: "", parent_email: "",
                      lifeguard_id: "", time: "8:30am",
                      dates: ["", "", "", ""],
                    });
                    fetchHsInvites();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to send invite.");
                  } finally {
                    setHsSubmitting(false);
                  }
                }}
              >
                {hsSubmitting ? "Sending…" : "Send Invite Email"}
              </Button>
            </div>

            <div className="card">
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-bold text-slate-800">Recent Invites</h2>
                <Button variant="ghost" size="sm" onClick={fetchHsInvites} data-testid="hs-refresh">Refresh</Button>
              </div>
              {hsInvites.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No invites yet. Create one above.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2">Child</th>
                        <th className="text-left p-2">Parent Email</th>
                        <th className="text-left p-2">Instructor</th>
                        <th className="text-left p-2">Time</th>
                        <th className="text-left p-2">Dates</th>
                        <th className="text-left p-2">Total</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Link</th>
                        <th className="text-left p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {hsInvites.map(inv => {
                        const url = `${window.location.origin}/half-season/${inv.token}`;
                        return (
                          <tr key={inv.token} className="border-t" data-testid={`hs-row-${inv.token}`}>
                            <td className="p-2 font-semibold">{inv.child_first_name} {inv.child_last_name}</td>
                            <td className="p-2">{inv.parent_email}</td>
                            <td className="p-2">{inv.lifeguard_name}</td>
                            <td className="p-2">{inv.time}</td>
                            <td className="p-2 text-xs">{(inv.dates || []).length} dates</td>
                            <td className="p-2 font-bold">${(inv.total_price || 0).toFixed(2)}</td>
                            <td className="p-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                inv.status === "completed" ? "bg-green-100 text-green-700" :
                                inv.status === "cancelled" ? "bg-slate-100 text-slate-500" :
                                "bg-amber-100 text-amber-700"
                              }`}>{inv.status}</span>
                            </td>
                            <td className="p-2">
                              <button
                                onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }}
                                className="text-xs text-[#2B4C8C] hover:underline"
                                data-testid={`hs-copy-${inv.token}`}
                              >Copy link</button>
                            </td>
                            <td className="p-2">
                              {inv.status === "sent" && (
                                <button
                                  onClick={async () => {
                                    if (!window.confirm("Cancel this invite?")) return;
                                    try {
                                      await axios.delete(`${API}/admin/half-season/invites/${inv.token}`);
                                      fetchHsInvites();
                                      toast.success("Invite cancelled");
                                    } catch (e) {
                                      toast.error(e.response?.data?.detail || "Failed");
                                    }
                                  }}
                                  className="text-xs text-red-600 hover:underline"
                                  data-testid={`hs-cancel-${inv.token}`}
                                >Cancel</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Lifeguards Tab */}
        {activeTab === "lifeguards" && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800 mb-6">
              Lifeguard Management
            </h1>

            <div className="card mb-6">
              <h3 className="font-heading font-bold text-slate-700 mb-3">Add New Lifeguard</h3>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <Input
                  data-testid="new-lifeguard-input"
                  placeholder="Name"
                  value={newLifeguard}
                  onChange={(e) => setNewLifeguard(e.target.value)}
                  className="flex-1"
                />
                <Input
                  data-testid="new-lifeguard-age"
                  placeholder="Age"
                  type="number"
                  value={newLifeguardAge}
                  onChange={(e) => setNewLifeguardAge(e.target.value)}
                  className="w-20"
                />
                <Select value={newLifeguardGender} onValueChange={setNewLifeguardGender}>
                  <SelectTrigger className="w-32" data-testid="new-lifeguard-gender">
                    <SelectValue placeholder="Gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={newLifeguardAgeGroup || ""} onValueChange={setNewLifeguardAgeGroup}>
                  <SelectTrigger className="w-32" data-testid="new-lifeguard-age-group">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Adult">Adult</SelectItem>
                    <SelectItem value="Teen">Teen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="mb-4">
                <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Availability</Label>
                <div className="space-y-2">
                  {DAYS.map(day => (
                    <div key={day} className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold text-slate-600 w-20 capitalize">{day}</span>
                      {getTimesForDayType(day).map(time => (
                        <label
                          key={`new-${day}-${time}`}
                          data-testid={`new-avail-${day}-${time.replace(':', '')}`}
                          className={`flex items-center gap-1 px-2 py-1 rounded-lg border cursor-pointer text-xs transition-all ${
                            isNewSlotSelected(day, time)
                              ? 'border-[#109FE9] bg-[#E6F5FD] text-[#109FE9] font-semibold'
                              : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isNewSlotSelected(day, time)}
                            onChange={() => toggleNewAvailability(day, time)}
                            className="sr-only"
                          />
                          {time}
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={handleAddLifeguard} className="btn-primary" data-testid="add-lifeguard-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Lifeguard
              </Button>
            </div>

            <div className="card overflow-x-auto">
              <table className="data-table min-w-[400px]">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Age</th>
                    <th>Gender</th>
                    <th>Type</th>
                    <th>Availability</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lifeguards.map(lg => (
                    <React.Fragment key={lg.id}>
                      <tr>
                        <td className="font-semibold">{lg.name}</td>
                        <td>{lg.age || '-'}</td>
                        <td>{lg.gender || '-'}</td>
                        <td>{lg.age_group || '-'}</td>
                        <td>
                          <span className="text-xs text-slate-500">
                            {(lg.availability || []).length} slots
                          </span>
                        </td>
                        <td>
                          {lg.active ? (
                            <span className="badge-green">Active</span>
                          ) : (
                            <span className="text-slate-400">Inactive</span>
                          )}
                        </td>
                        <td className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => editingLifeguardId === lg.id ? setEditingLifeguardId(null) : startEditAvailability(lg)}
                            className="text-[#109FE9] hover:text-[#0E8ACB]"
                            data-testid={`edit-avail-${lg.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteLifeguard(lg.id)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-lifeguard-${lg.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                      {editingLifeguardId === lg.id && (
                        <tr>
                          <td colSpan={7}>
                            <div className="p-4 bg-slate-50 rounded-xl">
                              <div className="flex flex-wrap items-center gap-3 mb-4">
                                <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Type</Label>
                                <Select value={editAgeGroup} onValueChange={setEditAgeGroup}>
                                  <SelectTrigger className="w-32" data-testid={`edit-age-group-${lg.id}`}>
                                    <SelectValue placeholder="Select type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Adult">Adult</SelectItem>
                                    <SelectItem value="Teen">Teen</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">
                                Edit Availability for {lg.name}
                              </Label>
                              <div className="space-y-2 mb-3">
                                {DAYS.map(day => (
                                  <div key={day} className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-semibold text-slate-600 w-20 capitalize">{day}</span>
                                    {getTimesForDayType(day).map(time => (
                                      <label
                                        key={`edit-${day}-${time}`}
                                        data-testid={`edit-avail-${lg.id}-${day}-${time.replace(':', '')}`}
                                        className={`flex items-center gap-1 px-2 py-1 rounded-lg border cursor-pointer text-xs transition-all ${
                                          isEditSlotSelected(day, time)
                                            ? 'border-[#109FE9] bg-[#E6F5FD] text-[#109FE9] font-semibold'
                                            : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isEditSlotSelected(day, time)}
                                          onChange={() => toggleEditAvailability(day, time)}
                                          className="sr-only"
                                        />
                                        {time}
                                      </label>
                                    ))}
                                  </div>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => saveEditAvailability(lg.id)} className="bg-[#109FE9] text-white hover:bg-[#0E8ACB]" data-testid={`save-avail-${lg.id}`}>
                                  <Save className="w-3 h-3 mr-1" /> Save
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingLifeguardId(null)} data-testid={`cancel-avail-${lg.id}`}>
                                  <X className="w-3 h-3 mr-1" /> Cancel
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {lifeguards.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-slate-500 py-8">
                        No lifeguards added yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Earnings Tab */}
        {activeTab === "earnings" && (
          <div className="animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800">
                Lifeguard Earnings
              </h1>
              <Button 
                onClick={() => window.open(`${API}/admin/earnings-report-pdf`, '_blank')}
                className="btn-primary w-full sm:w-auto"
                data-testid="print-earnings-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Print Earnings Report
              </Button>
              <Button 
                onClick={() => window.open(`${API}/admin/earnings-export-xlsx`, '_blank')}
                className="w-full sm:w-auto bg-[#2B4C8C] hover:bg-[#1e3666] text-white font-bold py-2 px-4 rounded-xl"
                data-testid="download-xlsx-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Download XLSX
              </Button>
            </div>

            {/* Camp Total Summary */}
            {earnings.camp_totals && (
              <div className="mb-6 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl p-4 md:p-6 shadow-lg">
                <h2 className="font-heading text-lg md:text-xl font-bold mb-4">Camp Total Summary (Paid Only)</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                  <div className="bg-white/20 rounded-xl p-3 md:p-4">
                    <p className="text-white/80 text-xs md:text-sm">Total Paid Lessons</p>
                    <p className="text-2xl md:text-3xl font-bold">{earnings.camp_totals.total_lessons}</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 md:p-4">
                    <p className="text-white/80 text-xs md:text-sm">Total Camp Earnings</p>
                    <p className="text-2xl md:text-3xl font-bold">${earnings.camp_totals.total_earnings?.toFixed(2)}</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 md:p-4">
                    <p className="text-white/80 text-xs md:text-sm">Rate Per Lesson</p>
                    <p className="text-2xl md:text-3xl font-bold">$40.00</p>
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Select value={filterDay} onValueChange={setFilterDay}>
                <SelectTrigger className="w-full sm:w-40" data-testid="earnings-filter-day">
                  <SelectValue placeholder="Filter by day" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Days</SelectItem>
                  {DAYS.map(day => (
                    <SelectItem key={day} value={day}>
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterTime} onValueChange={setFilterTime}>
                <SelectTrigger className="w-full sm:w-40" data-testid="earnings-filter-time">
                  <SelectValue placeholder="Filter by time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Times</SelectItem>
                  {ALL_TIMES.map(time => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterInstructor} onValueChange={setFilterInstructor}>
                <SelectTrigger className="w-full sm:w-48" data-testid="earnings-filter-instructor">
                  <SelectValue placeholder="Filter by instructor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Instructors</SelectItem>
                  {lifeguards.map(lg => (
                    <SelectItem key={lg.id} value={lg.name}>{lg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Earnings by Instructor */}
            <div className="card">
              <h2 className="font-semibold text-lg text-slate-800 mb-4">Earnings by Instructor</h2>
              <p className="text-sm text-slate-600 mb-4">
                Earnings calculated at $40 per lesson. Sorted by instructor name.
              </p>
              
              {earnings.by_instructor?.length > 0 ? (
                <div className="space-y-6">
                  {earnings.by_instructor
                    .filter(e => filterInstructor === "all" || e.name === filterInstructor)
                    .map(instructor => {
                      // Filter lessons by day/time if filters are set
                      const filteredLessons = earnings.lessons_detail?.filter(l => 
                        l.instructor === instructor.name &&
                        (filterDay === "all" || l.day === filterDay) &&
                        (filterTime === "all" || l.time === filterTime)
                      ) || [];
                      
                      // Calculate filtered totals
                      const filteredTotalLessons = filteredLessons.reduce((sum, l) => sum + l.num_lessons, 0);
                      const filteredTotalEarnings = filteredLessons.reduce((sum, l) => sum + l.earnings, 0);
                      
                      if (filterDay !== "all" || filterTime !== "all") {
                        if (filteredLessons.length === 0) return null;
                      }
                      
                      return (
                        <div key={instructor.name} className="border border-slate-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-lg text-slate-800">{instructor.name}</h3>
                            <div className="text-right">
                              <p className="text-sm text-slate-600">
                                {filterDay === "all" && filterTime === "all" 
                                  ? `${instructor.paid_lessons} paid / ${instructor.lessons} total lessons`
                                  : `${filteredTotalLessons} lessons (filtered)`
                                }
                              </p>
                              <p className="text-xl font-bold text-green-600">
                                ${filterDay === "all" && filterTime === "all" 
                                  ? instructor.total_earnings?.toFixed(2)
                                  : filteredTotalEarnings.toFixed(2)
                                }
                              </p>
                            </div>
                          </div>
                          
                          {/* Day/Time Breakdown */}
                          {filterDay === "all" && filterTime === "all" && (
                            <div className="grid grid-cols-2 gap-4 mb-3">
                              <div>
                                <p className="text-xs text-slate-500 font-semibold mb-1">By Day:</p>
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(instructor.by_day || {}).map(([day, data]) => (
                                    <span key={day} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                      {day.charAt(0).toUpperCase() + day.slice(1)}: {data.lessons} (${data.earnings})
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 font-semibold mb-1">By Time:</p>
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(instructor.by_time || {}).map(([time, data]) => (
                                    <span key={time} className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                                      {time}: {data.lessons} (${data.earnings})
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div className="overflow-x-auto">
                          <table className="data-table text-sm min-w-[500px]">
                            <thead>
                              <tr>
                                <th>Child</th>
                                <th>Day</th>
                                <th>Time</th>
                                <th>Lessons</th>
                                <th>Earnings</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(filterDay === "all" && filterTime === "all" 
                                ? earnings.lessons_detail?.filter(l => l.instructor === instructor.name)
                                : filteredLessons
                              )?.map((lesson, idx) => (
                                <tr key={idx}>
                                  <td>{lesson.child_name}</td>
                                  <td>{lesson.day?.charAt(0).toUpperCase() + lesson.day?.slice(1)}</td>
                                  <td>{lesson.time}</td>
                                  <td className="text-center">{lesson.num_lessons}</td>
                                  <td className="text-center font-semibold text-green-600">${lesson.earnings?.toFixed(2)}</td>
                                  <td>
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      lesson.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {lesson.status?.toUpperCase()}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">
                  No earnings data yet. Assign instructors to registrations.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800 mb-6">
              Settings
            </h1>

            <div className="card max-w-md">
              <h2 className="font-semibold text-lg text-slate-800 mb-4">
                Capacity Settings
              </h2>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                  Maximum Students Per Time Slot
                </Label>
                <div className="flex gap-4 items-center">
                  <Input
                    data-testid="capacity-input"
                    type="number"
                    min="1"
                    max="50"
                    value={settings.max_capacity_per_slot}
                    onChange={(e) => setSettings({ ...settings, max_capacity_per_slot: parseInt(e.target.value) || 10 })}
                    className="w-24"
                  />
                  <Button 
                    onClick={() => handleUpdateCapacity(settings.max_capacity_per_slot)}
                    className="btn-primary"
                    data-testid="save-capacity-btn"
                  >
                    <Save className="w-4 h-4 mr-2" /> Save
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  This controls the maximum number of students that can register for each day/time slot.
                </p>
              </div>
            </div>

            {/* CamperSnapshot Bulk Re-Sync */}
            <div className="card max-w-md mt-6 border-2 border-amber-300">
              <h2 className="font-semibold text-lg text-slate-800 mb-2">
                Re-sync All Lessons to CamperSnapshot
              </h2>
              <p className="text-sm text-slate-600 mb-3">
                Re-sends every paid lesson + every one-off cancellation / reschedule that we ever pushed to CamperSnapshot. Use this only if CamperSnapshot lost data and needs to be rebuilt. Safe to run multiple times.
              </p>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 mb-3">
                <strong>Heads up:</strong> This can take 30 seconds to several minutes depending on how many lessons exist. Stay on this page until it finishes.
              </p>
              <Button
                data-testid="resync-camper-snapshot-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white w-full font-bold"
                onClick={async () => {
                  if (!window.confirm("Re-push EVERY paid lesson and replay every reschedule/cancel to CamperSnapshot now?")) return;
                  const tId = toast.loading("Re-syncing to CamperSnapshot... this may take a minute.");
                  try {
                    const res = await axios.post(`${API}/admin/camper-snapshot/resync-all`, {}, { timeout: 10 * 60 * 1000 });
                    const d = res.data || {};
                    toast.dismiss(tId);
                    toast.success(
                      `Re-sync complete: ${d.permanent_enrollment_pushed || 0}/${d.total_paid_registrations || 0} enrollments pushed, ${d.one_off_cancellations_replayed || 0} cancellations + ${d.one_off_reschedules_replayed || 0} reschedules replayed. Failures: ${d.permanent_enrollment_failed || 0}. Skipped (no person_id): ${d.permanent_enrollment_skipped_no_person_id || 0}.`,
                      { duration: 15000 }
                    );
                    if ((d.failures || []).length > 0) {
                      console.warn("[Resync failures]", d.failures);
                    }
                  } catch (e) {
                    toast.dismiss(tId);
                    toast.error(e.response?.data?.detail || "Re-sync failed. Check backend logs.");
                  }
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Re-sync All to CamperSnapshot
              </Button>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === "reports" && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800 mb-6">
              Reports
            </h1>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="card">
                <h2 className="font-semibold text-lg text-slate-800 mb-4">
                  Available Lessons
                </h2>
                <p className="text-sm text-slate-600 mb-4">
                  Download a PDF showing all available lesson slots sorted by lifeguard, day, and time.
                </p>
                <Button 
                  onClick={() => window.open(`${API}/admin/available-lessons-pdf`, '_blank')}
                  className="btn-primary w-full"
                  data-testid="available-lessons-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Available Lessons PDF
                </Button>
              </div>

              <div className="card">
                <h2 className="font-semibold text-lg text-slate-800 mb-4">
                  Daily Lesson Report
                </h2>
                <p className="text-sm text-slate-600 mb-4">
                  Generate a PDF report of all lessons scheduled for a specific date.
                </p>
                <div>
                  <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                    Select Date (MM/DD/YYYY)
                  </Label>
                  <div className="flex gap-4">
                    <Input
                      data-testid="report-date-input"
                      placeholder="07/01/2026"
                      value={reportDate}
                      onChange={(e) => setReportDate(e.target.value)}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleGenerateReport}
                      className="btn-primary"
                      data-testid="generate-report-btn"
                    >
                      <Download className="w-4 h-4 mr-2" /> Download
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== NON-ENROLLED TABS ===== */}
        {activeTab && activeTab.startsWith("ne_") && (
          <div className="mb-4 flex items-center gap-3 flex-wrap" data-testid="ne-session-toggle-bar">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Session:</span>
            {[
              { key: "june", label: "June (Pre Season)" },
              { key: "july_august", label: "July-August (In Season)" },
            ].map(s => (
              <button
                key={s.key}
                onClick={() => setNeSession(s.key)}
                data-testid={`ne-session-pill-${s.key}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border-2 transition ${
                  neSession === s.key
                    ? "bg-[#FDF6E3] text-[#92400E] font-bold border-[#D4A843]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === "ne_dashboard" && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800 mb-6">Non-Enrolled Dashboard</h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="card text-center p-4"><p className="text-2xl font-bold text-[#2B4C8C]">{neSummary.total_registrations || 0}</p><p className="text-xs text-slate-500">Total</p></div>
              <div className="card text-center p-4"><p className="text-2xl font-bold text-yellow-600">{neSummary.tentative || 0}</p><p className="text-xs text-slate-500">Tentative</p></div>
              <div className="card text-center p-4"><p className="text-2xl font-bold text-green-600">{neSummary.confirmed || 0}</p><p className="text-xs text-slate-500">Confirmed</p></div>
              <div className="card text-center p-4"><p className="text-2xl font-bold text-[#D4A843]">${neSummary.total_revenue || 0}</p><p className="text-xs text-slate-500">Revenue</p></div>
            </div>
            <h2 className="font-semibold text-slate-700 mb-3">Recent Registrations</h2>
            <div className="space-y-2">
              {neRegistrations.slice(0, 10).map(r => (
                <div key={r.id} className="card p-3 flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-sm">{r.child_first_name} {r.child_last_name}</p>
                    <p className="text-xs text-slate-500">{r.choice1_day} {r.choice1_time} — {r.choice1_lifeguard_name}</p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded ${r.status === "confirmed" ? "bg-green-100 text-green-700" : r.status === "tentative" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>
                    {r.status?.toUpperCase()}
                  </span>
                </div>
              ))}
              {neRegistrations.length === 0 && <p className="text-slate-400 text-center py-8">No registrations yet.</p>}
            </div>
          </div>
        )}

        {activeTab === "ne_registrations" && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800 mb-4">Non-Enrolled Registrations</h1>
            <div className="space-y-2">
              {neRegistrations.sort((a,b) => `${a.child_last_name}`.localeCompare(`${b.child_last_name}`)).map(r => {
                // Effective status: if signed but somehow still tentative, treat as signed
                const eff = (r.signature || r.signed_at) && r.status === "tentative" ? "signed" : r.status;
                const canMarkPaid = eff === "signed";  // ONLY signed contracts can be marked paid
                return (
                <div
                  key={r.id}
                  className="card p-4 flex justify-between items-center cursor-pointer hover:bg-slate-50"
                  onClick={() => setSelectedNeRegistration(r)}
                  data-testid={`ne-reg-card-${r.id}`}
                >
                  <div>
                    <p className="font-semibold">{r.child_first_name} {r.child_last_name}</p>
                    <p className="text-xs text-slate-500">{r.parent_first_name} {r.parent_last_name} — {r.email}</p>
                    <p className="text-xs text-slate-500">{r.choice1_day} {r.choice1_time} — {r.choice1_lifeguard_name} — ${r.total_price}</p>
                  </div>
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      eff === "confirmed" ? "bg-red-200 text-red-900" :
                      eff === "signed" ? "bg-yellow-200 text-yellow-900" :
                      eff === "tentative" ? "bg-yellow-100 text-yellow-800" :
                      eff === "cancelled" ? "bg-orange-100 text-orange-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>
                      {eff?.toUpperCase()}
                    </span>
                    {canMarkPaid && (
                      <Button size="sm" onClick={async () => { await axios.post(`${API}/admin/non-enrolled/registrations/${r.id}/mark-paid`, {registration_id: r.id}); toast.success("Marked paid"); fetchData(); }} className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`ne-mark-paid-${r.id}`}>Mark Paid</Button>
                    )}
                    {(eff === "cancelled" || eff === "expired") && (
                      <Button
                        size="sm"
                        onClick={async () => {
                          const markPaid = window.confirm("Reinstate this registration?\n\nClick OK to reinstate AND mark as paid.\nClick Cancel to reinstate without marking paid (returns to signed/tentative).");
                          try {
                            const res = await axios.post(`${API}/admin/non-enrolled/registrations/${r.id}/reinstate`, { mark_paid: markPaid, payment_reference: markPaid ? "cash-reinstate" : null });
                            toast.success(`Reinstated as ${res.data.status.toUpperCase()}`);
                            fetchNeData();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to reinstate");
                          }
                        }}
                        className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                        data-testid={`ne-reinstate-${r.id}`}
                      >Reinstate</Button>
                    )}
                    {eff !== "cancelled" && eff !== "expired" && (
                      <Button size="sm" variant="ghost" onClick={async () => { await axios.post(`${API}/admin/non-enrolled/registrations/${r.id}/cancel`); toast.success("Cancelled"); fetchData(); }} className="text-red-500 text-xs">Cancel</Button>
                    )}
                  </div>
                </div>
                );
              })}
              {neRegistrations.length === 0 && <p className="text-slate-400 text-center py-8">No registrations.</p>}
            </div>
          </div>
        )}

        {activeTab === "ne_slots" && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800 mb-4">Non-Enrolled Lesson Slots</h1>

            {/* ===== BULK SCHEDULE BUILDER ===== */}
            <div className="card p-5 mb-6 border-l-4 border-[#D4A843]">
              <h3 className="font-bold text-lg mb-1 text-[#2B4C8C]">Build Lifeguard Schedule</h3>
              <p className="text-xs text-slate-500 mb-4">
                Pick a lifeguard, the days they're working, and the times they're offering.
                We'll create one recurring slot per day/time combination and auto-calculate the total
                price = price-per-lesson × number of that weekday between the start and end dates.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Lifeguard</Label>
                  <Select value={neBulk.lifeguard_id} onValueChange={v => setNeBulk(p => ({...p, lifeguard_id: v}))}>
                    <SelectTrigger data-testid="ne-bulk-lifeguard" className="mt-1"><SelectValue placeholder="Select lifeguard" /></SelectTrigger>
                    <SelectContent>
                      {neLifeguards.filter(l => l.active).map(l => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Price / Lesson ($)</Label>
                  <Input
                    data-testid="ne-bulk-price"
                    type="number"
                    step="1"
                    className="mt-1"
                    value={neBulk.price_per_lesson}
                    onChange={e => setNeBulk(p => ({...p, price_per_lesson: parseFloat(e.target.value) || 0}))}
                  />
                  {(() => {
                    const sel = neBulk.availability;
                    if (!sel.length) return null;
                    const all430 = sel.every(s => s.time === "4:30pm");
                    const any430 = sel.some(s => s.time === "4:30pm");
                    const suggested = all430 ? (neSettings.late_afternoon_price || 70) : (neSettings.price_per_lesson || 75);
                    if (Number(neBulk.price_per_lesson) !== Number(suggested)) {
                      return (
                        <button
                          type="button"
                          onClick={() => setNeBulk(p => ({...p, price_per_lesson: suggested}))}
                          className="text-xs text-amber-700 hover:underline mt-1 block"
                          data-testid="ne-bulk-apply-suggested-price"
                        >
                          {all430
                            ? `Use $${suggested}/lesson (4:30 PM rate)`
                            : any430
                              ? `Heads up: mix of 4:30 ($${neSettings.late_afternoon_price || 70}) and other times ($${neSettings.price_per_lesson || 75}). Create them in separate batches for correct pricing, or use $${suggested}.`
                              : `Use $${suggested}/lesson (standard rate)`}
                        </button>
                      );
                    }
                    return (
                      <p className="text-xs text-slate-500 mt-1">
                        {all430 ? "Using 4:30 PM rate" : "Using standard rate"}
                      </p>
                    );
                  })()}
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Season Start</Label>
                  <Input
                    data-testid="ne-bulk-start"
                    type="date"
                    className="mt-1"
                    value={neBulk.season_start_date}
                    onChange={e => setNeBulk(p => ({...p, season_start_date: e.target.value}))}
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Season End</Label>
                  <Input
                    data-testid="ne-bulk-end"
                    type="date"
                    className="mt-1"
                    value={neBulk.season_end_date}
                    onChange={e => setNeBulk(p => ({...p, season_end_date: e.target.value}))}
                  />
                </div>
              </div>

              <div className="mb-4">
                <Label className="text-xs font-bold uppercase tracking-wider mb-2 block">Availability (check the times for each day)</Label>
                <div className="space-y-2">
                  {NE_DAYS.map(d => (
                    <div key={d.key} className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold text-slate-600 w-24 capitalize">{d.short}</span>
                      {NE_TIMES.map(time => {
                        const selected = isNeAvailSelected(d.key, time);
                        return (
                          <label
                            key={`ne-avail-${d.key}-${time}`}
                            data-testid={`ne-avail-${d.key}-${time.replace(':', '')}`}
                            className={`flex items-center gap-1 px-2 py-1 rounded-lg border cursor-pointer text-xs transition-all ${
                              selected
                                ? 'border-[#D4A843] bg-[#FDF6E3] text-[#92400E] font-semibold'
                                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleNeAvail(d.key, time)}
                              className="sr-only"
                            />
                            {time}
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Live preview */}
              {(() => {
                if (!neBulk.availability.length || !neBulk.season_start_date || !neBulk.season_end_date) return null;
                const dowIdx = {monday:1,tuesday:2,wednesday:3,thursday:4,friday:5,saturday:6,sunday:0};
                const countWeekday = (startStr, endStr, dayKey) => {
                  const target = dowIdx[dayKey];
                  const start = new Date(startStr + "T00:00:00");
                  const end = new Date(endStr + "T00:00:00");
                  if (isNaN(start) || isNaN(end) || end < start) return 0;
                  let delta = (target - start.getDay() + 7) % 7;
                  const first = new Date(start);
                  first.setDate(first.getDate() + delta);
                  if (first > end) return 0;
                  return Math.floor((end - first) / (7 * 86400000)) + 1;
                };
                // Group by day for preview
                const grouped = {};
                neBulk.availability.forEach(({day, time}) => {
                  if (!grouped[day]) grouped[day] = [];
                  grouped[day].push(time);
                });
                const rows = Object.entries(grouped).map(([day, times]) => ({
                  day,
                  count: countWeekday(neBulk.season_start_date, neBulk.season_end_date, day),
                  times: times.sort(),
                }));
                return (
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm" data-testid="ne-bulk-preview">
                    <p className="font-semibold text-slate-700 mb-1">Preview</p>
                    <ul className="text-xs text-slate-600 space-y-0.5">
                      {rows.map(r => (
                        <li key={r.day}>
                          <span className="capitalize font-medium">{r.day}</span> ({r.times.join(", ")}): {r.count} lesson{r.count !== 1 ? "s" : ""} × ${neBulk.price_per_lesson} = <span className="font-bold text-[#2B4C8C]">${(r.count * neBulk.price_per_lesson).toFixed(2)}</span> per family
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-xs text-slate-500">
                      Will create <span className="font-bold text-slate-700">{neBulk.availability.length}</span> recurring slot{neBulk.availability.length !== 1 ? "s" : ""}.
                    </p>
                  </div>
                );
              })()}

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="ne-bulk-replace"
                    checked={neBulk.replace_existing}
                    onChange={e => setNeBulk(p => ({...p, replace_existing: e.target.checked}))}
                  />
                  Replace this lifeguard's existing slots
                </label>
                <Button
                  data-testid="ne-bulk-create-btn"
                  onClick={async () => {
                    if (!neBulk.lifeguard_id) { toast.error("Pick a lifeguard"); return; }
                    if (!neBulk.availability.length) { toast.error("Pick at least one day/time"); return; }
                    if (!neBulk.season_end_date) { toast.error("Set a season end date"); return; }
                    try {
                      const payload = {
                        lifeguard_id: neBulk.lifeguard_id,
                        availability: neBulk.availability,
                        price_per_lesson: neBulk.price_per_lesson,
                        season_start_date: neBulk.season_start_date,
                        season_end_date: neBulk.season_end_date,
                        replace_existing: neBulk.replace_existing,
                        session: neSession,
                      };
                      const res = await axios.post(`${API}/admin/non-enrolled/lesson-slots/bulk`, payload);
                      toast.success(`Created ${res.data.created_count} new + updated ${res.data.updated_count} slots`);
                      setNeBulk(p => ({...p, availability: [], replace_existing: false}));
                      fetchData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to create slots");
                    }
                  }}
                  className="bg-[#2B4C8C] hover:bg-[#1e3666] text-white ml-auto"
                >
                  Create Schedule
                </Button>
              </div>
            </div>

            {/* ===== EXISTING SLOTS ===== */}
            <h3 className="font-semibold text-sm text-slate-700 mb-2">Active Slots ({neLessonSlots.filter(s => s.is_active !== false).length})</h3>
            <div className="space-y-2">
              {neLessonSlots
                .filter(s => s.is_active !== false)
                .sort((a, b) => {
                  const dorder = {monday:0,tuesday:1,wednesday:2,thursday:3,friday:4,saturday:5,sunday:6};
                  const da = dorder[a.day_of_week] ?? 9, db = dorder[b.day_of_week] ?? 9;
                  if (da !== db) return da - db;
                  return (a.time || "").localeCompare(b.time || "");
                })
                .map(s => (
                <div key={s.id} className="card p-3 flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-sm">{s.day_of_week?.charAt(0).toUpperCase()+s.day_of_week?.slice(1)} — {s.time}</p>
                    <p className="text-xs text-slate-500">
                      {s.lifeguard_name} — <span className="font-bold text-[#2B4C8C]">${s.price}</span>
                      {s.lesson_count ? <span className="text-slate-400"> ({s.lesson_count} × ${s.price_per_lesson || (s.price / s.lesson_count).toFixed(2)})</span> : null}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={async () => { await axios.delete(`${API}/admin/non-enrolled/lesson-slots/${s.id}`); toast.success("Removed"); fetchData(); }} className="text-red-500 text-xs">Remove</Button>
                </div>
              ))}
              {neLessonSlots.filter(s => s.is_active !== false).length === 0 && <p className="text-slate-400 text-center py-8">No slots yet. Use the schedule builder above.</p>}
            </div>
          </div>
        )}

        {activeTab === "ne_lifeguards" && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800 mb-4">Non-Enrolled Lifeguards</h1>
            <div className="card p-4 mb-6">
              <h3 className="font-semibold text-sm mb-3">Add Lifeguard</h3>
              <div className="flex gap-2 items-end">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input value={neNewLgName} onChange={e => setNeNewLgName(e.target.value)} placeholder="Name" className="w-48" />
                </div>
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={neNewLgType} onValueChange={setNeNewLgType}>
                    <SelectTrigger className="w-28"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Adult">Adult</SelectItem>
                      <SelectItem value="Teen">Teen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={async () => {
                  if (!neNewLgName.trim()) return;
                  await axios.post(`${API}/admin/non-enrolled/lifeguards`, {name: neNewLgName, age_group: neNewLgType || null});
                  setNeNewLgName(""); setNeNewLgType("");
                  toast.success("Lifeguard added"); fetchData();
                }} className="bg-[#D4A843] hover:bg-[#b8902e] text-white text-sm">Add</Button>
              </div>
            </div>
            <div className="space-y-2">
              {neLifeguards.map(lg => (
                <div key={lg.id} className="card p-3 flex justify-between items-center">
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{lg.name}</p>
                    <p className="text-xs text-slate-500">{lg.age_group || "N/A"} — {lg.active ? "Active" : "Inactive"}{lg.notes ? ` — ${lg.notes}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-[#2B4C8C] text-xs"
                      data-testid={`ne-edit-lg-${lg.id}`}
                      onClick={() => setNeEditLg({
                        id: lg.id,
                        name: lg.name || "",
                        age_group: lg.age_group || "",
                        active: lg.active !== false,
                        notes: lg.notes || "",
                      })}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={async () => {
                      if (!confirm(`Delete ${lg.name}? This cannot be undone.`)) return;
                      await axios.delete(`${API}/admin/non-enrolled/lifeguards/${lg.id}`);
                      toast.success("Lifeguard removed"); fetchData();
                    }} className="text-red-500 text-xs"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
              {neLifeguards.length === 0 && <p className="text-slate-400 text-center py-8">No lifeguards. Add one above.</p>}
            </div>
          </div>
        )}

        {activeTab === "ne_settings" && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800 mb-4">Non-Enrolled Settings</h1>
            <div className="card p-6 max-w-md space-y-4">
              <div className="flex items-center justify-between">
                <Label>Registration Open</Label>
                <input type="checkbox" checked={neSettings.registration_open ?? true} onChange={e => setNeSettings(p => ({...p, registration_open: e.target.checked}))} className="w-5 h-5" />
              </div>
              <div>
                <Label>Mon-Thu Default Price ($)</Label>
                <Input type="number" value={neSettings.default_price_mth || ""} onChange={e => setNeSettings(p => ({...p, default_price_mth: parseFloat(e.target.value)}))} />
              </div>
              <div>
                <Label>Friday Default Price ($)</Label>
                <Input type="number" value={neSettings.default_price_fri || ""} onChange={e => setNeSettings(p => ({...p, default_price_fri: parseFloat(e.target.value)}))} />
              </div>
              <div>
                <Label>Saturday Default Price ($)</Label>
                <Input type="number" value={neSettings.default_price_sat || ""} onChange={e => setNeSettings(p => ({...p, default_price_sat: parseFloat(e.target.value)}))} />
              </div>
              <div>
                <Label>Tentative Hold (days)</Label>
                <Input type="number" value={neSettings.tentative_hold_days || 7} onChange={e => setNeSettings(p => ({...p, tentative_hold_days: parseInt(e.target.value)}))} />
              </div>

              <div className="border-t pt-4">
                <Label className="font-bold text-slate-800">4:30 PM Late-Afternoon Price ($/lesson)</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Special non-enrolled rate for M-F 4:30 PM lessons during the In-Season (July-August). Auto-applied when admin creates 4:30 slots via the bulk builder.
                </p>
                <Input
                  data-testid="ne-late-afternoon-price"
                  type="number"
                  step="1"
                  value={neSettings.late_afternoon_price || ""}
                  placeholder="70"
                  onChange={e => setNeSettings(p => ({...p, late_afternoon_price: parseFloat(e.target.value)}))}
                />
              </div>

              <div className="border-t pt-4 space-y-4">
                <div>
                  <Label className="font-bold text-slate-800">Per-Session Enrollment Toggles</Label>
                  <p className="text-xs text-slate-500">Turn enrollments on/off per off-season session. Optional auto-open date will flip the switch ON automatically on that date.</p>
                </div>

                {/* June */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-semibold">June (Pre Season)</Label>
                    <button
                      type="button"
                      data-testid="ne-june-enabled-toggle"
                      onClick={() => setNeSettings(p => ({...p, june_enabled: !p.june_enabled}))}
                      className={`px-4 py-1.5 rounded-full text-sm font-bold border-2 transition ${
                        neSettings.june_enabled
                          ? "bg-green-100 text-green-700 border-green-500"
                          : "bg-slate-100 text-slate-600 border-slate-300"
                      }`}
                    >
                      {neSettings.june_enabled ? "On" : "Off"}
                    </button>
                  </div>
                  <Label className="text-xs text-slate-500">Auto-open date (optional)</Label>
                  <Input
                    type="date"
                    data-testid="ne-june-auto-open"
                    value={neSettings.june_auto_open_date || ""}
                    onChange={e => setNeSettings(p => ({...p, june_auto_open_date: e.target.value || null}))}
                  />
                </div>

                {/* July-August */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-semibold">July-August (In Season)</Label>
                    <button
                      type="button"
                      data-testid="ne-ja-enabled-toggle"
                      onClick={() => setNeSettings(p => ({...p, july_august_enabled: !p.july_august_enabled}))}
                      className={`px-4 py-1.5 rounded-full text-sm font-bold border-2 transition ${
                        neSettings.july_august_enabled
                          ? "bg-green-100 text-green-700 border-green-500"
                          : "bg-slate-100 text-slate-600 border-slate-300"
                      }`}
                    >
                      {neSettings.july_august_enabled ? "On" : "Off"}
                    </button>
                  </div>
                  <Label className="text-xs text-slate-500">Auto-open date (optional)</Label>
                  <Input
                    type="date"
                    data-testid="ne-ja-auto-open"
                    value={neSettings.july_august_auto_open_date || ""}
                    onChange={e => setNeSettings(p => ({...p, july_august_auto_open_date: e.target.value || null}))}
                  />
                </div>

                {/* September */}
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-semibold">September (Off Season)</Label>
                    <button
                      type="button"
                      data-testid="ne-sep-enabled-toggle"
                      onClick={() => setNeSettings(p => ({...p, september_enabled: !p.september_enabled}))}
                      className={`px-4 py-1.5 rounded-full text-sm font-bold border-2 transition ${
                        neSettings.september_enabled
                          ? "bg-green-100 text-green-700 border-green-500"
                          : "bg-slate-100 text-slate-600 border-slate-300"
                      }`}
                    >
                      {neSettings.september_enabled ? "On" : "Off"}
                    </button>
                  </div>
                  <Label className="text-xs text-slate-500">Auto-open date (e.g. 2026-08-01)</Label>
                  <Input
                    type="date"
                    data-testid="ne-sep-auto-open"
                    value={neSettings.september_auto_open_date || ""}
                    onChange={e => setNeSettings(p => ({...p, september_auto_open_date: e.target.value || null}))}
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    September session config (dates, skip dates) will be added when you’re ready. The toggle and auto-open already work for it.
                  </p>
                </div>
              </div>

              <Button onClick={async () => { await axios.put(`${API}/admin/non-enrolled/settings`, neSettings); toast.success("Saved"); fetchData(); }} className="bg-[#2B4C8C] hover:bg-[#1e3666] text-white w-full">Save Settings</Button>
            </div>
          </div>
        )}

        {/* Non-Enrolled Calendar */}
        {activeTab === "ne_calendar" && (
          <div className="animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800">
                {neSessionLabel} Lesson Calendar
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-green-100 border-2 border-green-500" />
                  <span className="text-xs text-slate-600">Open</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-yellow-100 border-2 border-yellow-400" />
                  <span className="text-xs text-slate-600">Tentative / Signed (Unpaid)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-red-100 border-2 border-red-500" />
                  <span className="text-xs text-slate-600">Paid</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-orange-100 border-2 border-orange-400" />
                  <span className="text-xs text-slate-600">Cancelled</span>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Select value={filterInstructor} onValueChange={setFilterInstructor}>
                <SelectTrigger className="w-full sm:w-48" data-testid="ne-filter-instructor">
                  <SelectValue placeholder="Filter by instructor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Instructors</SelectItem>
                  {neLifeguards.map(lg => (
                    <SelectItem key={lg.id} value={lg.id}>{lg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Calendar Grid - Date × Time, mirror of enrolled */}
            {(() => {
              const NE_TIMES_CAL = ["2:00pm", "2:30pm", "3:00pm", "3:30pm", "4:00pm", "4:30pm", "5:00pm"];
              const dowNames = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
              const sessionDefaults = NE_SESSION_DEFAULTS[neSession];
              const startStr = sessionDefaults.season_start_date;
              const endStr = sessionDefaults.season_end_date;
              const skipDates = neSession === "july_august" ? ["2026-07-03", "2026-07-04"] : [];
              const start = new Date(startStr + "T00:00:00");
              const end = new Date(endStr + "T00:00:00");
              const dates = [];
              if (!isNaN(start) && !isNaN(end)) {
                for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                  const dow = d.getDay();
                  // Only weekdays (Mon-Fri); allow Saturday only if session permits it
                  const allowSat = sessionDefaults.saturday_allowed;
                  if ((dow >= 1 && dow <= 5) || (allowSat && dow === 6)) {
                    const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                    if (!skipDates.includes(iso)) dates.push(new Date(d));
                  }
                }
              }

              // Build map of slots by (day, time) -> [slots]
              const slotsByDayTime = {};
              neLessonSlots.filter(s => s.is_active !== false).forEach(s => {
                const key = `${s.day_of_week}|${s.time}`;
                if (!slotsByDayTime[key]) slotsByDayTime[key] = [];
                if (filterInstructor === "all" || s.lifeguard_id === filterInstructor) {
                  slotsByDayTime[key].push(s);
                }
              });

              const fmtDate = (d) => `${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")}/${d.getFullYear()}`;

              return (
                <div className="card overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="p-3 text-left text-sm font-semibold text-slate-600">Date</th>
                        {NE_TIMES_CAL.map(time => (
                          <th key={time} className="p-3 text-center text-sm font-semibold text-slate-600">{time}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dates.map(date => {
                        const dateStr = fmtDate(date);
                        const dayName = dowNames[date.getDay()];
                        return (
                          <tr key={dateStr} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="p-3">
                              <div className="font-semibold text-slate-800">{dateStr}</div>
                              <div className="text-xs text-slate-500 capitalize">{dayName}</div>
                            </td>
                            {NE_TIMES_CAL.map(time => {
                              const slotsHere = slotsByDayTime[`${dayName}|${time}`] || [];
                              if (slotsHere.length === 0) {
                                return (
                                  <td key={time} className="p-2">
                                    <div className="calendar-cell bg-slate-50 text-center opacity-30">
                                      <span className="text-xs text-slate-300">—</span>
                                    </div>
                                  </td>
                                );
                              }
                              // Determine status by booking
                              const slotBookings = slotsHere.map(s => {
                                const r = neRegistrations.find(reg =>
                                  reg.choice1_day === s.day_of_week &&
                                  reg.choice1_time === s.time &&
                                  reg.choice1_lifeguard_id === s.lifeguard_id &&
                                  reg.status !== "cancelled" && reg.status !== "expired"
                                );
                                // Compute effective status for visual logic
                                const effStatus = r && (r.signature || r.signed_at) && r.status === "tentative" ? "signed" : r?.status;
                                return { slot: s, reg: r, effStatus };
                              });
                              const anyPaid = slotBookings.some(b => b.effStatus === "confirmed");
                              const anySigned = slotBookings.some(b => b.effStatus === "signed");
                              const anyTentative = slotBookings.some(b => b.effStatus === "tentative");
                              const allOpen = slotBookings.every(b => !b.reg);
                              const cellClass = allOpen
                                ? "calendar-cell-open"
                                : anyPaid
                                  ? "calendar-cell-paid"
                                  : anySigned
                                    ? "calendar-cell-signed"
                                    : anyTentative
                                      ? "calendar-cell-reserved"
                                      : "calendar-cell-reserved";

                              return (
                                <td key={time} className="p-2">
                                  <div
                                    className={`calendar-cell ${cellClass}`}
                                    data-testid={`ne-slot-${dayName}-${time}`}
                                    onClick={() => setSelectedSlot({
                                      ne: true,
                                      date: dateStr,
                                      day: dayName,
                                      time,
                                      slots: slotBookings,
                                    })}
                                  >
                                    {allOpen ? (
                                      <div className="text-center">
                                        <span className="text-xs text-slate-500">
                                          {slotsHere.length} open
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="text-left space-y-1">
                                        {slotBookings.map(({slot, reg, effStatus}) => (
                                          <div
                                            key={slot.id}
                                            className={`text-xs px-1.5 py-0.5 rounded ${
                                              !reg ? "bg-green-100 text-green-800" :
                                              effStatus === "confirmed" ? "bg-red-200 text-red-900" :
                                              effStatus === "signed" ? "bg-yellow-200 text-yellow-900" :
                                              effStatus === "tentative" ? "bg-yellow-100 text-yellow-800" :
                                              "bg-orange-100 text-orange-800"
                                            }`}
                                          >
                                            {reg ? `${reg.child_first_name} ${reg.child_last_name}` : `Open (${slot.lifeguard_name})`}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {dates.length === 0 && (
                    <p className="text-slate-400 text-center py-8">Set the June season dates in Settings.</p>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Non-Enrolled Earnings */}
        {activeTab === "ne_earnings" && (
          <div className="animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800">Non-Enrolled Earnings</h1>
              <div className="flex gap-2">
                <Button 
                  onClick={() => window.open(`${API}/admin/non-enrolled/reports/earnings-xlsx?session=${neSession}`, '_blank')}
                  className="w-full sm:w-auto bg-[#2B4C8C] hover:bg-[#1e3666] text-white font-bold py-2 px-4 rounded-xl"
                  data-testid="ne-download-xlsx"
                >
                  <Download className="w-4 h-4 mr-2" /> Download XLSX
                </Button>
              </div>
            </div>

            {/* Green Summary Banner */}
            {(() => {
              // Off-season (June + future September) per-lesson split:
              // $30 to the camp, $40 to the lifeguard.
              // July-August keeps the legacy single-total earnings view.
              // Mirrors backend constants CAMP_SHARE_PER_LESSON / LIFEGUARD_SHARE_PER_LESSON
              // and uses_split() in non_enrolled.py.
              const CAMP_PER_LESSON = 30;
              const LG_PER_LESSON = 40;
              const SPLIT_SESSIONS = new Set(["june", "september"]);
              const splitMode = SPLIT_SESSIONS.has(neSession);

              if (splitMode) {
                const neEarningsData = {};
                let totalPaidLessons = 0;
                let totalPaidCamp = 0;
                let totalPaidLifeguard = 0;
                let totalPotentialCamp = 0;
                let totalPotentialLifeguard = 0;
                neRegistrations.filter(r => r.status === "confirmed" || r.status === "tentative").forEach(r => {
                  const lg = r.choice1_lifeguard_name || "Unknown";
                  const lessons = r.lesson_count || 1;
                  const campAmt = lessons * CAMP_PER_LESSON;
                  const lgAmt = lessons * LG_PER_LESSON;
                  if (!neEarningsData[lg]) neEarningsData[lg] = { lifeguard_paid: 0, camp_paid: 0, lifeguard_potential: 0, camp_potential: 0, paid_count: 0, total_count: 0, paid_lessons: 0, total_lessons: 0 };
                  neEarningsData[lg].lifeguard_potential += lgAmt;
                  neEarningsData[lg].camp_potential += campAmt;
                  neEarningsData[lg].total_count += 1;
                  neEarningsData[lg].total_lessons += lessons;
                  totalPotentialCamp += campAmt;
                  totalPotentialLifeguard += lgAmt;
                  if (r.status === "confirmed") {
                    neEarningsData[lg].lifeguard_paid += lgAmt;
                    neEarningsData[lg].camp_paid += campAmt;
                    neEarningsData[lg].paid_count += 1;
                    neEarningsData[lg].paid_lessons += lessons;
                    totalPaidLessons += lessons;
                    totalPaidCamp += campAmt;
                    totalPaidLifeguard += lgAmt;
                  }
                });
                return (
                  <>
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white mb-6">
                      <h2 className="text-lg font-bold mb-1">Total Summary (Paid Only)</h2>
                      <p className="text-xs opacity-80 mb-4">Per-lesson split: ${CAMP_PER_LESSON} camp + ${LG_PER_LESSON} lifeguard · {neSessionLabel}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-white/10 rounded-xl p-4" data-testid="ne-earn-paid-lessons">
                          <p className="text-sm opacity-80">Paid Lessons</p>
                          <p className="text-3xl font-bold">{totalPaidLessons}</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4" data-testid="ne-earn-camp">
                          <p className="text-sm opacity-80">Camp Earnings</p>
                          <p className="text-3xl font-bold">${totalPaidCamp.toFixed(2)}</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4" data-testid="ne-earn-lifeguards">
                          <p className="text-sm opacity-80">Lifeguard Earnings</p>
                          <p className="text-3xl font-bold">${totalPaidLifeguard.toFixed(2)}</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4" data-testid="ne-earn-combined">
                          <p className="text-sm opacity-80">Combined</p>
                          <p className="text-3xl font-bold">${(totalPaidCamp + totalPaidLifeguard).toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs opacity-90">
                        <div>Potential Camp: <span className="font-bold">${totalPotentialCamp.toFixed(2)}</span></div>
                        <div>Potential Lifeguard: <span className="font-bold">${totalPotentialLifeguard.toFixed(2)}</span></div>
                        <div>Potential Combined: <span className="font-bold">${(totalPotentialCamp + totalPotentialLifeguard).toFixed(2)}</span></div>
                      </div>
                    </div>

                    {/* Earnings by Instructor */}
                    <div className="card p-6">
                      <h2 className="font-heading text-lg font-bold text-slate-800 mb-2">Earnings by Instructor</h2>
                      <p className="text-sm text-slate-500 mb-4">${CAMP_PER_LESSON} per lesson goes to the camp; ${LG_PER_LESSON} per lesson goes to the lifeguard.</p>
                      {Object.keys(neEarningsData).length === 0 ? (
                        <p className="text-slate-400 text-center py-6">No earnings data yet. Assign instructors to registrations.</p>
                      ) : (
                        <div className="grid gap-4">
                          {Object.entries(neEarningsData).sort(([a],[b]) => a.localeCompare(b)).map(([name, data]) => (
                            <div key={name} className="border border-slate-200 rounded-xl p-4" data-testid={`ne-earn-row-${name}`}>
                              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 gap-1">
                                <h3 className="font-bold text-slate-800">{name}</h3>
                                <div className="flex flex-wrap gap-3 text-sm">
                                  <span className="font-bold text-green-700">LG paid: ${data.lifeguard_paid.toFixed(2)}</span>
                                  <span className="font-bold text-blue-700">Camp paid: ${data.camp_paid.toFixed(2)}</span>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
                                <div><span className="text-slate-500">Reservations:</span> <span className="font-semibold">{data.total_count}</span></div>
                                <div><span className="text-slate-500">Paid Lessons:</span> <span className="font-semibold text-green-600">{data.paid_lessons}</span></div>
                                <div><span className="text-slate-500">Total Lessons:</span> <span className="font-semibold">{data.total_lessons}</span></div>
                                <div><span className="text-slate-500">LG Potential:</span> <span className="font-semibold">${data.lifeguard_potential.toFixed(2)}</span></div>
                                <div><span className="text-slate-500">Camp Potential:</span> <span className="font-semibold">${data.camp_potential.toFixed(2)}</span></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                );
              }

              // Legacy July-August view: total_price-based
              const neEarningsData = {};
              let totalPaidEarnings = 0;
              neRegistrations.filter(r => r.status === "confirmed" || r.status === "tentative").forEach(r => {
                const lg = r.choice1_lifeguard_name || "Unknown";
                if (!neEarningsData[lg]) neEarningsData[lg] = { confirmed: 0, potential: 0, paid_count: 0, total_count: 0 };
                neEarningsData[lg].potential += (r.total_price || 0);
                neEarningsData[lg].total_count += 1;
                if (r.status === "confirmed") {
                  neEarningsData[lg].confirmed += (r.total_price || 0);
                  neEarningsData[lg].paid_count += 1;
                  totalPaidEarnings += (r.total_price || 0);
                }
              });
              const totalPotential = Object.values(neEarningsData).reduce((s, d) => s + d.potential, 0);
              return (
                <>
                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white mb-6">
                    <h2 className="text-lg font-bold mb-1">Total Summary (Paid Only)</h2>
                    <p className="text-xs opacity-80 mb-4">{neSessionLabel}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="bg-white/10 rounded-xl p-4">
                        <p className="text-sm opacity-80">Total Paid Lessons</p>
                        <p className="text-3xl font-bold">{Object.values(neEarningsData).reduce((s,d) => s+d.paid_count, 0)}</p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-4">
                        <p className="text-sm opacity-80">Total Earnings</p>
                        <p className="text-3xl font-bold">${totalPaidEarnings.toFixed(2)}</p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-4">
                        <p className="text-sm opacity-80">Potential Earnings</p>
                        <p className="text-3xl font-bold">${totalPotential.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="card p-6">
                    <h2 className="font-heading text-lg font-bold text-slate-800 mb-2">Earnings by Instructor</h2>
                    <p className="text-sm text-slate-500 mb-4">Sorted by instructor name.</p>
                    {Object.keys(neEarningsData).length === 0 ? (
                      <p className="text-slate-400 text-center py-6">No earnings data yet. Assign instructors to registrations.</p>
                    ) : (
                      <div className="grid gap-4">
                        {Object.entries(neEarningsData).sort(([a],[b]) => a.localeCompare(b)).map(([name, data]) => (
                          <div key={name} className="border border-slate-200 rounded-xl p-4">
                            <div className="flex justify-between items-center mb-2">
                              <h3 className="font-bold text-slate-800">{name}</h3>
                              <span className="text-sm font-bold text-green-600">${data.confirmed.toFixed(2)} paid</span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                              <div><span className="text-slate-500">Total Lessons:</span> <span className="font-semibold">{data.total_count}</span></div>
                              <div><span className="text-slate-500">Paid:</span> <span className="font-semibold text-green-600">{data.paid_count}</span></div>
                              <div><span className="text-slate-500">Potential:</span> <span className="font-semibold">${data.potential.toFixed(2)}</span></div>
                              <div><span className="text-slate-500">Unpaid:</span> <span className="font-semibold text-yellow-600">{data.total_count - data.paid_count}</span></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Non-Enrolled Reports */}
        {activeTab === "ne_reports" && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800 mb-6">Non-Enrolled Reports</h1>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="card">
                <h2 className="font-semibold text-lg text-slate-800 mb-4">
                  Available Lessons
                </h2>
                <p className="text-sm text-slate-600 mb-4">
                  View all open non-enrolled lesson slots by day, time, and lifeguard.
                </p>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left font-semibold text-slate-600">Day</th>
                        <th className="p-2 text-left font-semibold text-slate-600">Time</th>
                        <th className="p-2 text-left font-semibold text-slate-600">Lifeguard</th>
                        <th className="p-2 text-left font-semibold text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {neLessonSlots.filter(s => s.is_active !== false)
                        .sort((a, b) => ["monday","tuesday","wednesday","thursday","friday","saturday"].indexOf(a.day_of_week) - ["monday","tuesday","wednesday","thursday","friday","saturday"].indexOf(b.day_of_week))
                        .map(slot => {
                          const booked = neRegistrations.some(r => r.choice1_day === slot.day_of_week && r.choice1_time === slot.time && r.choice1_lifeguard_id === slot.lifeguard_id && r.status !== "cancelled" && r.status !== "expired");
                          return (
                            <tr key={slot.id} className="border-b border-slate-100">
                              <td className="p-2">{slot.day_of_week?.charAt(0).toUpperCase() + slot.day_of_week?.slice(1)}</td>
                              <td className="p-2">{slot.time}</td>
                              <td className="p-2">{slot.lifeguard_name}</td>
                              <td className="p-2">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${booked ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                                  {booked ? "BOOKED" : "OPEN"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="card">
                <h2 className="font-semibold text-lg text-slate-800 mb-4">
                  Registration Summary
                </h2>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-slate-600">Total Registrations</span><span className="font-bold">{neSummary.total_registrations || 0}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Tentative (Pending Payment)</span><span className="font-bold text-yellow-600">{neSummary.tentative || 0}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Confirmed (Paid)</span><span className="font-bold text-green-600">{neSummary.confirmed || 0}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Cancelled</span><span className="font-bold text-red-600">{neSummary.cancelled || 0}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Expired</span><span className="font-bold text-slate-400">{neSummary.expired || 0}</span></div>
                  <div className="flex justify-between border-t pt-3 mt-3"><span className="text-slate-800 font-bold">Total Revenue</span><span className="font-bold text-[#2B4C8C] text-lg">${neSummary.total_revenue || 0}</span></div>
                </div>
              </div>
            </div>

            <div className="card mt-6">
              <h2 className="font-semibold text-lg text-slate-800 mb-4">Registrations by Day</h2>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                {["monday","tuesday","wednesday","thursday","friday","saturday"].map(day => {
                  const count = neRegistrations.filter(r => r.choice1_day === day && r.status !== "cancelled" && r.status !== "expired").length;
                  return (
                    <div key={day} className="text-center p-4 bg-slate-50 rounded-xl">
                      <p className="text-xs text-slate-500 font-semibold">{day.charAt(0).toUpperCase() + day.slice(1)}</p>
                      <p className="text-2xl font-bold text-[#2B4C8C]">{count}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <Button onClick={() => window.open(`${API}/admin/non-enrolled/reports/earnings-xlsx?session=${neSession}`, '_blank')} className="btn-primary">
                <Download className="w-4 h-4 mr-2" /> Download Full Earnings XLSX
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Daily Roster PDF Modal */}
      <Dialog open={rosterOpen} onOpenChange={setRosterOpen}>
        <DialogContent className="max-w-md" data-testid="roster-pdf-dialog">
          <DialogHeader>
            <DialogTitle>Download Daily Roster PDF</DialogTitle>
            <DialogDescription>
              Pick a season and a day. You&apos;ll get a printable PDF per day (or a ZIP of 5 PDFs if you choose &quot;All Days&quot;).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-bold text-slate-700">Season</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  data-testid="roster-season-camp"
                  onClick={() => setRosterSeason("camp")}
                  className={`px-3 py-3 rounded-xl border-2 text-sm font-bold transition ${
                    rosterSeason === "camp"
                      ? "bg-[#2B4C8C] text-white border-[#2B4C8C]"
                      : "bg-white text-slate-700 border-slate-300 hover:border-[#2B4C8C]"
                  }`}
                >
                  Camp Season<br /><span className="text-xs font-normal opacity-80">(Enrolled)</span>
                </button>
                <button
                  type="button"
                  data-testid="roster-season-non"
                  onClick={() => setRosterSeason("non_enrolled")}
                  className={`px-3 py-3 rounded-xl border-2 text-sm font-bold transition ${
                    rosterSeason === "non_enrolled"
                      ? "bg-[#D4A843] text-white border-[#D4A843]"
                      : "bg-white text-slate-700 border-slate-300 hover:border-[#D4A843]"
                  }`}
                >
                  Non Season<br /><span className="text-xs font-normal opacity-80">(Non-Enrolled)</span>
                </button>
              </div>
            </div>

            {rosterSeason === "non_enrolled" && (
              <div>
                <Label className="text-sm font-bold text-slate-700">Non-Enrolled Session</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button
                    type="button"
                    data-testid="roster-session-june"
                    onClick={() => setRosterSession("june")}
                    className={`px-3 py-2 rounded-xl border-2 text-xs font-bold transition ${
                      rosterSession === "june"
                        ? "bg-amber-100 text-amber-800 border-amber-500"
                        : "bg-white text-slate-700 border-slate-300"
                    }`}
                  >June (Pre Season)</button>
                  <button
                    type="button"
                    data-testid="roster-session-ja"
                    onClick={() => setRosterSession("july_august")}
                    className={`px-3 py-2 rounded-xl border-2 text-xs font-bold transition ${
                      rosterSession === "july_august"
                        ? "bg-blue-100 text-blue-800 border-blue-500"
                        : "bg-white text-slate-700 border-slate-300"
                    }`}
                  >July-August (In Season)</button>
                </div>
              </div>
            )}

            <div>
              <Label className="text-sm font-bold text-slate-700">Date</Label>
              <Input
                type="date"
                data-testid="roster-date"
                value={rosterDate}
                onChange={e => setRosterDate(e.target.value)}
                className="mt-1"
              />
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="roster-whole-week"
                  checked={rosterWholeWeek}
                  onChange={e => setRosterWholeWeek(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-slate-700">
                  Download whole week (Mon–Fri of that date's week) as a ZIP
                </span>
              </label>
              <p className="text-xs text-slate-500 mt-2">
                Roster reflects per-date cancels and reschedules at the moment you click Download.
              </p>
            </div>

            <Button
              data-testid="roster-download-btn"
              className="w-full bg-[#2B4C8C] hover:bg-[#1e3666] text-white font-bold"
              onClick={() => {
                if (!rosterDate) {
                  toast.error("Pick a date first");
                  return;
                }
                const params = new URLSearchParams({ season: rosterSeason });
                if (rosterWholeWeek) {
                  params.set("week_start", rosterDate);
                } else {
                  params.set("date", rosterDate);
                }
                if (rosterSeason === "non_enrolled") params.set("session", rosterSession);
                window.open(`${API}/admin/daily-roster-pdf?${params.toString()}`, "_blank");
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Download {rosterWholeWeek ? "ZIP (5 PDFs)" : "PDF"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Slot Detail Modal */}
      <Dialog open={!!selectedSlot} onOpenChange={() => setSelectedSlot(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedSlot?.date} at {selectedSlot?.time}
            </DialogTitle>
            <DialogDescription>
              {selectedSlot?.ne
                ? `${selectedSlot?.slots?.length || 0} slot(s) — ${selectedSlot?.slots?.filter(b => b.reg).length || 0} booked`
                : `${selectedSlot?.registrations?.length || 0} student(s) registered`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {selectedSlot?.ne ? (
              // Non-enrolled detail view
              <>
                {selectedSlot?.slots?.map(({slot, reg}) => {
                  const eff = reg && (reg.signature || reg.signed_at) && reg.status === "tentative" ? "signed" : reg?.status;
                  const canMarkPaid = reg && eff === "signed";  // ONLY signed contracts can be marked paid
                  return (
                  <div key={slot.id} className={`p-4 rounded-lg ${reg ? "bg-slate-50" : "bg-green-50"}`}>
                    <div className="flex justify-between items-start">
                      <div
                        className={reg ? "cursor-pointer hover:underline" : ""}
                        onClick={reg ? () => { setSelectedSlot(null); setSelectedNeRegistration(reg); } : undefined}
                        data-testid={reg ? `ne-slot-open-detail-${reg.id}` : undefined}
                      >
                        <p className="font-semibold">{slot.lifeguard_name} — ${slot.price} total</p>
                        {reg ? (
                          <>
                            <p className="text-sm text-slate-700 mt-1">Booked by: <b>{reg.child_first_name} {reg.child_last_name}</b></p>
                            <p className="text-sm text-slate-600">Parent: {reg.parent_first_name} {reg.parent_last_name}</p>
                            <p className="text-sm text-slate-600">Phone: {reg.phone}</p>
                            <p className="text-sm text-slate-600">Email: {reg.email}</p>
                          </>
                        ) : (
                          <p className="text-sm text-green-700 mt-1">Open — available for booking</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {reg ? (
                          <span className={`text-xs font-bold px-2 py-1 rounded ${
                            eff === "confirmed" ? "bg-red-200 text-red-900" :
                            eff === "signed" ? "bg-yellow-200 text-yellow-900" :
                            eff === "tentative" ? "bg-yellow-100 text-yellow-800" :
                            eff === "cancelled" ? "bg-orange-100 text-orange-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>{eff.toUpperCase()}</span>
                        ) : (
                          <span className="badge-green text-xs">OPEN</span>
                        )}
                        {canMarkPaid && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white text-xs h-7"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/admin/non-enrolled/registrations/${reg.id}/mark-paid`, {
                                  registration_id: reg.id, payment_reference: "cash"
                                });
                                toast.success("Marked as paid");
                                setSelectedSlot(null);
                                fetchData();
                              } catch { toast.error("Failed"); }
                            }}
                          >Mark Paid</Button>
                        )}
                        {reg && eff !== "cancelled" && eff !== "expired" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 text-xs h-7"
                            onClick={async () => {
                              if (!confirm(`Cancel ${reg.child_first_name}'s booking?`)) return;
                              try {
                                await axios.post(`${API}/admin/non-enrolled/registrations/${reg.id}/cancel`);
                                toast.success("Cancelled");
                                setSelectedSlot(null);
                                fetchData();
                              } catch { toast.error("Failed"); }
                            }}
                          >Cancel</Button>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
                {(!selectedSlot?.slots || selectedSlot.slots.length === 0) && (
                  <p className="text-center text-slate-500 py-8">No slots offered at this time</p>
                )}
              </>
            ) : (
              <>
                {selectedSlot?.registrations?.map(reg => (
                  <div key={reg.id} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{reg.child_name}</p>
                        <p className="text-sm text-slate-600">Parent: {reg.parent_name}</p>
                        <p className="text-sm text-slate-600">Phone: {reg.phone}</p>
                        <p className="text-sm text-slate-600">
                          Instructor: {reg.instructor || "Not assigned"}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {statusBadge(reg.status)}
                        <Button
                          size="sm"
                          className="bg-[#109FE9] hover:bg-[#0E8ACB] text-white text-xs h-7"
                          data-testid={`manage-lesson-${reg.id}`}
                          onClick={async () => {
                            try {
                              const res = await axios.get(`${API}/registration/${reg.id}`);
                              setSelectedSlot(null);
                              setSelectedRegistration(res.data);
                            } catch (err) {
                              toast.error("Failed to load registration details");
                            }
                          }}
                        >
                          <Edit className="w-3 h-3 mr-1" /> Manage Lessons
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {(!selectedSlot?.registrations || selectedSlot.registrations.length === 0) && (
                  <p className="text-center text-slate-500 py-8">No students registered for this slot</p>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Registration Detail Modal */}
      <Dialog open={!!selectedRegistration} onOpenChange={() => setSelectedRegistration(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedRegistration?.child_first_name} {selectedRegistration?.child_last_name}
            </DialogTitle>
            <DialogDescription>
              Registration Details
            </DialogDescription>
          </DialogHeader>
          {selectedRegistration && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Parent Name</Label>
                  <p className="font-semibold">{selectedRegistration.parent_first_name} {selectedRegistration.parent_last_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p>{selectedRegistration.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Phone</Label>
                  <p>{selectedRegistration.phone}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Address</Label>
                  <p>
                    {selectedRegistration.address}<br />
                    {selectedRegistration.city}, {selectedRegistration.state} {selectedRegistration.zip_code}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Swim Level</Label>
                  <p>{selectedRegistration.swim_level}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Lifeguard Preference</Label>
                  <p>{selectedRegistration.lifeguard_preference}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Previous Instructor</Label>
                  <p>{selectedRegistration.previous_instructor || "N/A"}</p>
                </div>
              </div>
              
              {/* Additional Child Information */}
              {selectedRegistration.additional_child_info && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-slate-500 mb-2 block">Additional Child Information</Label>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-blue-800 text-sm">{selectedRegistration.additional_child_info}</p>
                  </div>
                </div>
              )}

              {/* Lesson Details */}
              <div className="border-t pt-4">
                <Label className="text-xs text-slate-500 mb-2 block">Lesson Details</Label>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-3">
                  <p className="text-green-800 font-semibold text-lg">
                    {(selectedRegistration.confirmed_day || selectedRegistration.choice1_day)?.charAt(0).toUpperCase() + (selectedRegistration.confirmed_day || selectedRegistration.choice1_day)?.slice(1)} at {selectedRegistration.confirmed_time || selectedRegistration.choice1_time}
                  </p>
                  <p className="text-green-700 mt-1">
                    ${(selectedRegistration.confirmed_price || selectedRegistration.choice1_price)?.toFixed(2)} - {(selectedRegistration.confirmed_dates || selectedRegistration.choice1_dates)?.length} lessons remaining
                  </p>
                  <p className="text-green-600 text-sm mt-1">
                    Lifeguard: {selectedRegistration.assigned_instructor || selectedRegistration.lifeguard_preference}
                  </p>
                </div>

                {/* Individual Lesson Dates */}
                <Label className="text-xs text-slate-500 mb-2 block">Individual Lessons</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {(selectedRegistration.confirmed_dates || selectedRegistration.choice1_dates)?.map((date, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2">
                      <span className="text-sm font-medium text-slate-700" data-testid={`lesson-date-${idx}`}>{date}</span>
                      {lessonAction?.date === date ? (
                        <div className="flex-1 ml-3">
                          {lessonAction.action === "delete" && (
                            <div className="space-y-2">
                              <div className="flex gap-1">
                                <Button size="sm" className="h-7 text-xs bg-red-500 hover:bg-red-600 text-white" data-testid={`confirm-delete-lesson-${idx}`}
                                  onClick={() => {
                                    handleDeleteLesson(selectedRegistration.id, date, "cancelled", false);
                                    setLessonAction(null);
                                  }}>
                                  Confirm Cancel
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setLessonAction(null)}>Back</Button>
                              </div>
                            </div>
                          )}
                          {lessonAction.action === "reschedule" && (
                            <div className="space-y-2">
                              <Input
                                placeholder="New date (MM/DD/YYYY)"
                                value={rescheduleDate}
                                onChange={(e) => setRescheduleDate(e.target.value)}
                                className="h-8 text-xs"
                                data-testid={`reschedule-date-input-${idx}`}
                              />
                              <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="New time (optional)" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="8:30am">8:30am</SelectItem>
                                  <SelectItem value="4:00pm">4:00pm</SelectItem>
                                  <SelectItem value="4:30pm">4:30pm</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select value={rescheduleInstructor} onValueChange={setRescheduleInstructor}>
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="New instructor (optional)" />
                                </SelectTrigger>
                                <SelectContent>
                                  {lifeguards.map(lg => (
                                    <SelectItem key={lg.id} value={lg.name}>{lg.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select value={lessonReason} onValueChange={setLessonReason}>
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="rescheduled">Rescheduled</SelectItem>
                                  <SelectItem value="camp_cancelled">Camp Cancelled</SelectItem>
                                  <SelectItem value="lifeguard_cancelled">Lifeguard Cancelled</SelectItem>
                                  <SelectItem value="parent_cancelled">Parent Cancelled</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex gap-1">
                                <Button size="sm" className="h-7 text-xs bg-[#109FE9] hover:bg-[#0E8ACB] text-white" data-testid={`confirm-reschedule-lesson-${idx}`}
                                  onClick={() => {
                                    if (!rescheduleDate) { toast.error("Enter a new date"); return; }
                                    handleRescheduleLesson(selectedRegistration.id, date, rescheduleDate, rescheduleTime, rescheduleInstructor, lessonReason);
                                    setLessonAction(null);
                                    setRescheduleDate("");
                                    setRescheduleTime("");
                                    setRescheduleInstructor("");
                                  }}>
                                  Confirm Reschedule
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setLessonAction(null); setRescheduleDate(""); setRescheduleTime(""); setRescheduleInstructor(""); }}>Cancel</Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-700" data-testid={`delete-lesson-${idx}`}
                            onClick={() => { setLessonAction({ date, action: "delete" }); setLessonReason("camp_cancelled"); setLessonRefund(false); }}>
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-[#109FE9] hover:text-[#0E8ACB]" data-testid={`reschedule-lesson-${idx}`}
                            onClick={() => { setLessonAction({ date, action: "reschedule" }); setLessonReason("rescheduled"); }}>
                            <Edit className="w-3 h-3 mr-1" /> Reschedule
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Modification History */}
                {(selectedRegistration.lesson_modifications || []).length > 0 && (
                  <div className="mt-3">
                    <Label className="text-xs text-slate-500 mb-1 block">Modification History</Label>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {(selectedRegistration.lesson_modifications || []).map((mod, idx) => (
                        <div key={idx} className="text-xs text-slate-500 bg-slate-50 rounded px-2 py-1">
                          {mod.action === "deleted" && (
                            <span>{mod.date} — Deleted ({mod.reason?.replace('_', ' ')}){mod.refund ? ' [Refund]' : ''}</span>
                          )}
                          {mod.action === "rescheduled" && (
                            <span>{mod.original_date} → {mod.new_date}{mod.new_time ? ` at ${mod.new_time}` : ''}{mod.new_instructor ? ` w/ ${mod.new_instructor}` : ''} ({mod.reason?.replace('_', ' ')})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <Label className="text-xs text-slate-500 mb-2 block">Signature Status</Label>
                <div className="flex items-center justify-between">
                  <p>
                    {selectedRegistration.signed_at 
                      ? `Signed on ${new Date(selectedRegistration.signed_at).toLocaleDateString()}`
                      : "Not signed yet"}
                  </p>
                  {selectedRegistration.signed_at && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.open(`${API}/registration/${selectedRegistration.id}/contract-pdf`, '_blank');
                      }}
                      data-testid="download-contract-btn"
                    >
                      <Download className="w-4 h-4 mr-2" /> Download Contract
                    </Button>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-xs text-slate-500 mb-2 block">Assign Instructor</Label>
                <Select
                  value={selectedRegistration.assigned_instructor || ""}
                  onValueChange={(value) => {
                    handleAssignInstructor(selectedRegistration.id, value);
                    setSelectedRegistration({ ...selectedRegistration, assigned_instructor: value });
                  }}
                >
                  <SelectTrigger data-testid="assign-instructor-select">
                    <SelectValue placeholder="Select instructor" />
                  </SelectTrigger>
                  <SelectContent>
                    {lifeguards.map(lg => (
                      <SelectItem key={lg.id} value={lg.name}>{lg.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Base Time — fix if a reschedule clobbered it */}
              <div className="border-t pt-4">
                <Label className="text-xs text-slate-500 mb-2 block">
                  Base Time (default for all lessons — per-date reschedules stay in the modifications log)
                </Label>
                <div className="flex gap-2">
                  <Select
                    value={selectedRegistration.confirmed_time || ""}
                    onValueChange={async (value) => {
                      try {
                        await axios.post(`${API}/admin/registration/${selectedRegistration.id}/reset-base-time`, null, { params: { new_time: value } });
                        setSelectedRegistration({ ...selectedRegistration, confirmed_time: value });
                        toast.success("Base time updated");
                        fetchData();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Failed");
                      }
                    }}
                  >
                    <SelectTrigger data-testid="base-time-select"><SelectValue placeholder="Select base time" /></SelectTrigger>
                    <SelectContent>
                      {ALL_TIMES.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-slate-400 mt-1">Choice 1 was {selectedRegistration.choice1_day} at {selectedRegistration.choice1_time}.</p>
              </div>

              <div className="border-t pt-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="paid"
                    checked={selectedRegistration.status === "paid"}
                    onCheckedChange={(checked) => {
                      handleMarkPaid(selectedRegistration.id, checked);
                      setSelectedRegistration({ ...selectedRegistration, status: checked ? "paid" : "signed" });
                    }}
                    data-testid="mark-paid-checkbox"
                  />
                  <Label htmlFor="paid" className="font-semibold">
                    Mark as Paid in Full
                  </Label>
                </div>
                {statusBadge(selectedRegistration.status)}
              </div>

              {/* Delete Registration */}
              <div className="border-t pt-4 space-y-2">
                <Button
                  variant="outline"
                  className="w-full border-amber-500 text-amber-700 hover:bg-amber-50"
                  data-testid="resync-single-reg-btn"
                  onClick={async () => {
                    if (!window.confirm(`Re-push this registration (paid enrollment + all reschedules/cancels) to CamperSnapshot?`)) return;
                    const tid = toast.loading("Re-syncing to CamperSnapshot…");
                    try {
                      const res = await axios.post(`${API}/admin/registration/${selectedRegistration.id}/resync-camper-snapshot`);
                      toast.dismiss(tid);
                      toast.success(`Re-synced ${res.data.child_name} — ${(res.data.log || []).length} calls sent. See console for detail.`);
                      console.log("[Single-reg resync]", res.data);
                    } catch (e) {
                      toast.dismiss(tid);
                      toast.error(e.response?.data?.detail || "Re-sync failed.");
                    }
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Re-push to CamperSnapshot
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => handleDeleteRegistration(selectedRegistration.id)}
                  className="w-full bg-red-500 hover:bg-red-600"
                  data-testid="delete-registration-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete This Registration
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Non-Enrolled Registration Detail Modal ===== */}
      <Dialog open={!!selectedNeRegistration} onOpenChange={() => setSelectedNeRegistration(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedNeRegistration?.child_first_name} {selectedNeRegistration?.child_last_name}
            </DialogTitle>
            <DialogDescription>
              June Lesson Registration Details
            </DialogDescription>
          </DialogHeader>
          {selectedNeRegistration && (() => {
            const r = selectedNeRegistration;
            const eff = (r.signature || r.signed_at) && r.status === "tentative" ? "signed" : r.status;
            const dates = r.confirmed_dates || r.choice1_dates || [];
            return (
            <div className="space-y-4">
              {/* Status banner */}
              <div className={`rounded-xl p-4 border-2 ${
                eff === "confirmed" ? "bg-red-50 border-red-300" :
                eff === "signed" ? "bg-yellow-50 border-yellow-300" :
                eff === "tentative" ? "bg-yellow-50 border-yellow-300" :
                eff === "cancelled" ? "bg-orange-50 border-orange-300" :
                "bg-slate-50 border-slate-300"
              }`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-bold px-3 py-1 rounded ${
                    eff === "confirmed" ? "bg-red-200 text-red-900" :
                    eff === "signed" ? "bg-yellow-200 text-yellow-900" :
                    eff === "tentative" ? "bg-yellow-100 text-yellow-800" :
                    eff === "cancelled" ? "bg-orange-100 text-orange-700" :
                    "bg-slate-100 text-slate-600"
                  }`} data-testid="ne-detail-status">{eff?.toUpperCase()}</span>
                  <div className="flex gap-2">
                    {eff === "signed" && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid="ne-detail-mark-paid"
                        onClick={async () => {
                          await axios.post(`${API}/admin/non-enrolled/registrations/${r.id}/mark-paid`, {registration_id: r.id});
                          toast.success("Marked paid");
                          setSelectedNeRegistration(null);
                          fetchData();
                        }}
                      >Mark Paid</Button>
                    )}
                    {(eff === "cancelled" || eff === "expired") && (
                      <Button
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid="ne-detail-reinstate"
                        onClick={async () => {
                          const markPaid = window.confirm("Reinstate this registration?\n\nOK = reinstate AND mark as paid.\nCancel = reinstate without payment (returns to signed/tentative).");
                          try {
                            const res = await axios.post(`${API}/admin/non-enrolled/registrations/${r.id}/reinstate`, { mark_paid: markPaid, payment_reference: markPaid ? "cash-reinstate" : null });
                            toast.success(`Reinstated as ${res.data.status.toUpperCase()}`);
                            setSelectedNeRegistration(null);
                            fetchNeData();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to reinstate");
                          }
                        }}
                      >Reinstate</Button>
                    )}
                    {eff !== "cancelled" && eff !== "expired" && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="bg-red-500 hover:bg-red-600"
                        data-testid="ne-detail-cancel"
                        onClick={async () => {
                          if (!confirm(`Cancel ${r.child_first_name}'s booking?`)) return;
                          await axios.post(`${API}/admin/non-enrolled/registrations/${r.id}/cancel`);
                          toast.success("Cancelled");
                          setSelectedNeRegistration(null);
                          fetchData();
                        }}
                      >Cancel</Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Parent Name</Label>
                  <p className="font-semibold">{r.parent_first_name} {r.parent_last_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p className="text-sm break-all">{r.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Phone</Label>
                  <p>{r.phone}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Child DOB</Label>
                  <p>{r.child_dob || "—"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Swim Level</Label>
                  <p>{r.swim_level || "—"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Address</Label>
                  <p className="text-sm">
                    {r.address ? <>{r.address}<br />{r.city}, {r.state} {r.zip_code}</> : "—"}
                  </p>
                </div>
              </div>

              {r.notes && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-slate-500 mb-2 block">Parent Notes</Label>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-blue-800 text-sm">{r.notes}</p>
                  </div>
                </div>
              )}

              {/* Lesson Details */}
              <div className="border-t pt-4">
                <Label className="text-xs text-slate-500 mb-2 block">Lesson Details</Label>
                <div className="bg-[#FDF6E3] border border-[#D4A843] rounded-xl p-4 mb-3">
                  <p className="text-[#92400E] font-semibold text-lg">
                    {(r.confirmed_day || r.choice1_day)?.charAt(0).toUpperCase() + (r.confirmed_day || r.choice1_day)?.slice(1)}s at {r.confirmed_time || r.choice1_time}
                  </p>
                  <p className="text-[#92400E] mt-1">
                    ${Number(r.total_price).toFixed(2)} total — {dates.length} lesson{dates.length !== 1 ? "s" : ""}
                    {r.price_per_lesson ? <span className="text-slate-600"> (${r.price_per_lesson}/lesson)</span> : null}
                  </p>
                  <p className="text-[#92400E] text-sm mt-1">
                    Lifeguard: {r.assigned_instructor || r.choice1_lifeguard_name}
                  </p>
                </div>

                {dates.length > 0 && (
                  <>
                    <Label className="text-xs text-slate-500 mb-2 block">Individual Lessons</Label>
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto" data-testid="ne-detail-dates">
                      {dates.map((d, i) => (
                        <span key={i} className="px-3 py-1 bg-[#FDF6E3] border border-[#D4A843] rounded text-sm font-medium">
                          {d}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Signature */}
              <div className="border-t pt-4">
                <Label className="text-xs text-slate-500 mb-2 block">Signature Status</Label>
                <div className="flex items-center justify-between">
                  <p>
                    {r.signed_at
                      ? `Signed on ${new Date(r.signed_at).toLocaleDateString()}`
                      : "Not signed yet"}
                  </p>
                  {r.signed_at && (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="ne-detail-download-contract"
                      onClick={() => {
                        window.open(`${API}/non-enrolled/registration/${r.id}/contract-pdf`, '_blank');
                      }}
                    >
                      <Download className="w-4 h-4 mr-2" /> Download Signed Contract
                    </Button>
                  )}
                </div>
                {r.signature && r.signature_type === "typed" && (
                  <p className="text-xs text-slate-500 mt-2">Typed signature: <span className="italic font-semibold">{r.signature}</span></p>
                )}
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
      {/* ===== Non-Enrolled Lifeguard Edit Dialog ===== */}
      <Dialog open={!!neEditLg} onOpenChange={() => setNeEditLg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Lifeguard</DialogTitle>
            <DialogDescription>Update info for this non-enrolled lifeguard.</DialogDescription>
          </DialogHeader>
          {neEditLg && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Name</Label>
                <Input
                  data-testid="ne-edit-lg-name"
                  value={neEditLg.name}
                  onChange={e => setNeEditLg(p => ({...p, name: e.target.value}))}
                />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={neEditLg.age_group} onValueChange={v => setNeEditLg(p => ({...p, age_group: v}))}>
                  <SelectTrigger data-testid="ne-edit-lg-type"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Adult">Adult</SelectItem>
                    <SelectItem value="Teen">Teen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea
                  data-testid="ne-edit-lg-notes"
                  value={neEditLg.notes}
                  onChange={e => setNeEditLg(p => ({...p, notes: e.target.value}))}
                  rows={2}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="ne-edit-lg-active"
                  data-testid="ne-edit-lg-active"
                  checked={neEditLg.active}
                  onChange={e => setNeEditLg(p => ({...p, active: e.target.checked}))}
                  className="w-4 h-4"
                />
                <Label htmlFor="ne-edit-lg-active" className="text-sm cursor-pointer">Active (visible to parents)</Label>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => setNeEditLg(null)}>Cancel</Button>
                <Button
                  className="bg-[#2B4C8C] hover:bg-[#1e3666] text-white"
                  data-testid="ne-edit-lg-save"
                  onClick={async () => {
                    if (!neEditLg.name.trim()) { toast.error("Name is required"); return; }
                    try {
                      await axios.put(`${API}/admin/non-enrolled/lifeguards/${neEditLg.id}`, {
                        name: neEditLg.name.trim(),
                        age_group: neEditLg.age_group || null,
                        active: neEditLg.active,
                        notes: neEditLg.notes || null,
                      });
                      toast.success("Lifeguard updated");
                      setNeEditLg(null);
                      fetchData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Update failed");
                    }
                  }}
                >Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
